
export const SQL_INTERMEDIATE_PROBLEMS = [
  {
    id: 1,
    title: "Customers with Multiple Orders",
    difficulty: "Easy",
    slug: "sql-group-by-having-clause",
    seoTitle: "SQL GROUP BY and HAVING Clause",
    metaDescription: "Learn how to use SQL GROUP BY and HAVING to identify customers who have placed multiple orders.",
    tags: ["SQL", "GROUP BY", "HAVING", "COUNT"],
    description: "Find all customers who have placed more than one order.",
    explanation: "Use GROUP BY to create one group per customer and COUNT() to calculate the number of orders. Since COUNT() is an aggregate function, use HAVING instead of WHERE to return only customers whose order count is greater than one.",
    scenario: "The marketing team wants to reward loyal customers by sending a special discount to everyone who has placed multiple orders.",
    useCases: [
      "Customer loyalty analysis",
      "CRM segmentation",
      "Repeat customer identification"
    ],
    hint: "Join the customers and orders tables, group by each customer, count their orders, and filter using HAVING COUNT(order_id) > 1.",
    starterQuery: `SELECT
      c.customer_name,
      COUNT(o.order_id) AS order_count
  FROM customers c
  JOIN orders o
      ON c.customer_id = o.customer_id
  GROUP BY c.customer_name
  HAVING COUNT(o.order_id) > 1;`,
    expectedColumns: ["customer_name", "order_count"],
    solutionQuery: `SELECT
      c.customer_name,
      COUNT(o.order_id) AS order_count
  FROM customers c
  JOIN orders o
      ON c.customer_id = o.customer_id
  GROUP BY c.customer_id, c.customer_name
  HAVING COUNT(o.order_id) > 1;`,
  },
  {
    id: 2,
    title: "Running Total of Revenue",
    difficulty: "Easy",
    slug: "sql-running-total-window-function",
    seoTitle: "SQL Running Total Using Window Functions",
    metaDescription: "Learn how to calculate a cumulative running total using SQL window functions.",
  
    tags: ["SQL", "Window Functions", "SUM OVER", "Running Total"],
  
    description:
      "Calculate a running total of the total order amount, ordered by order_date.",
  
    explanation:
      "Window functions allow calculations across multiple rows without grouping them. Use SUM(total_amount) OVER (ORDER BY order_date) to calculate the cumulative revenue after each order.",
  
    scenario:
      "The finance team wants to track how total revenue has grown over time by calculating a cumulative running total for every order.",
  
    useCases: [
      "Revenue growth analysis",
      "Financial dashboards",
      "Cumulative sales reporting"
    ],
  
    hint:
      "Use SUM(total_amount) OVER (ORDER BY order_date) to calculate the running total.",
  
    starterQuery:
  `SELECT
      order_date,
      total_amount,
      SUM(total_amount) OVER (ORDER BY order_date) AS running_total
  FROM orders;`,
  
    expectedColumns: [
      "order_date",
      "total_amount",
      "running_total"
    ],
  
    solutionQuery:
  `SELECT
      order_date,
      total_amount,
      SUM(total_amount) OVER (ORDER BY order_date) AS running_total
  FROM orders;`,
  },
  {
    id: 3,
    title: "Highest Order Amount per Customer",
    difficulty: "Easy",
    slug: "sql-max-with-group-by",
    seoTitle: "SQL MAX Function with GROUP BY",
    metaDescription: "Learn how to find the highest order amount for each customer using SQL MAX() and GROUP BY.",
  
    tags: ["SQL", "MAX", "GROUP BY", "Aggregate Functions"],
  
    description:
      "Find the highest order amount placed by each customer. Return the customer's name and their maximum order amount.",
  
    explanation:
      "Use the MAX() aggregate function to find the largest total_amount within each customer's orders. GROUP BY creates one group per customer, and MAX() returns the highest order value from each group.",
  
    scenario:
      "The sales team wants to identify each customer's largest purchase to better understand spending behavior and identify high-value customers.",
  
    useCases: [
      "Customer spending analysis",
      "VIP customer identification",
      "Sales reporting"
    ],
  
    hint:
      "Join the customers and orders tables, group by each customer, and use MAX(total_amount) to find their largest order.",
  
    starterQuery: `SELECT
      c.customer_name,
      MAX(o.total_amount) AS max_spent
  FROM customers c
  JOIN orders o
      ON c.customer_id = o.customer_id
  GROUP BY
      c.customer_id,
      c.customer_name;`,
  
    expectedColumns: [
      "customer_name",
      "max_spent"
    ],
  
    solutionQuery: `SELECT
      c.customer_name,
      MAX(o.total_amount) AS max_spent
  FROM customers c
  JOIN orders o
      ON c.customer_id = o.customer_id
  GROUP BY
      c.customer_id,
      c.customer_name;`,
  },
  {
    id: 4,
    title: "Orders Above Average",
    difficulty: "Easy",
    slug: "sql-scalar-subquery",
    seoTitle: "SQL Scalar Subquery Tutorial",
    metaDescription: "Learn how to use a scalar subquery to find orders with a total amount greater than the average order value.",
  
    tags: ["SQL", "Subquery", "AVG", "Scalar Subquery"],
  
    description:
      "Find all orders whose total amount is greater than the average total amount of all orders.",
  
    explanation:
      "A scalar subquery returns a single value. First calculate the average order amount using AVG(total_amount), then compare each order's total_amount against that value in the WHERE clause.",
  
    scenario:
      "The finance team wants to identify high-value orders by comparing each order against the average order value across the business.",
  
    useCases: [
      "High-value order identification",
      "Dynamic filtering",
      "Revenue analysis"
    ],
  
    hint:
      "Use a scalar subquery: WHERE total_amount > (SELECT AVG(total_amount) FROM orders).",
  
    starterQuery: `SELECT
      order_id,
      customer_id,
      order_date,
      total_amount
  FROM orders
  WHERE total_amount > (
      SELECT AVG(total_amount)
      FROM orders
  );`,
  
    expectedColumns: [
      "order_id",
      "customer_id",
      "order_date",
      "total_amount"
    ],
  
    solutionQuery: `SELECT
      order_id,
      customer_id,
      order_date,
      total_amount
  FROM orders
  WHERE total_amount > (
      SELECT AVG(total_amount)
      FROM orders
  );`,
  },
  {
    id: 5,
    title: "Category Revenue Contribution",
    difficulty: "Easy",
    slug: "sql-join-with-sum-aggregation",
    seoTitle: "SQL JOIN with SUM Aggregation",
    metaDescription: "Learn how to join order_items and products to calculate total revenue by product category using SQL SUM().",
  
    tags: ["SQL", "JOIN", "SUM", "GROUP BY"],
  
    description:
      "Calculate the total revenue generated by each product category. Display the categories with the highest revenue first.",
  
    explanation:
      "Each order item belongs to a product, and each product belongs to a category. Join the order_items and products tables, group by category, and use SUM(total_price) to calculate the total revenue for each category.",
  
    scenario:
      "The business wants to understand which product categories generate the most revenue so they can prioritize marketing and inventory decisions.",
  
    useCases: [
      "Category revenue analysis",
      "Product performance reporting",
      "Business intelligence dashboards"
    ],
  
    hint:
      "Join order_items with products using product_id, group by category, and calculate SUM(total_price).",
  
    starterQuery: `SELECT
      p.category,
      SUM(oi.total_price) AS revenue
  FROM order_items oi
  JOIN products p
      ON oi.product_id = p.product_id
  GROUP BY
      p.category
  ORDER BY
      revenue DESC;`,
  
    expectedColumns: [
      "category",
      "revenue"
    ],
  
    solutionQuery: `SELECT
      p.category,
      SUM(oi.total_price) AS revenue
  FROM order_items oi
  JOIN products p
      ON oi.product_id = p.product_id
  GROUP BY
      p.category
  ORDER BY
      revenue DESC;`,
  },
  {
    id: 6,
    title: "Ranking Products by Price",
    difficulty: "Easy",
    slug: "sql-rank-partition-by",
    seoTitle: "SQL RANK Window Function with PARTITION BY",
    metaDescription: "Learn how to use the RANK() OVER window function with PARTITION BY to rank items within discrete groups.",
    tags: ["SQL", "RANK", "PARTITION BY"],
    description: "Rank products within each category from most expensive to least expensive using a window function.",
    explanation: "RANK() combined with PARTITION BY 'resets' the ranking for every new category, allowing you to find the #1 item in each department.",
    scenario: "You are building a catalog where each category starts with its 'Premium' offering at the top.",
    useCases: ["Category leaderboards", "Competitive pricing", "Organizing large catalogs"],
    hint: "Use RANK() OVER (PARTITION BY category ORDER BY price DESC)",
    starterQuery: "SELECT product_name, category, price, \nRANK() OVER (PARTITION BY category ORDER BY price DESC) as rank \nFROM products;",
    expectedColumns: ["product_name", "category", "price", "rank"],
    expectedRowCount: 5,
    solutionQuery: "SELECT product_name, category, price, RANK() OVER (PARTITION BY category ORDER BY price DESC) AS rank FROM products;",
  },
  {
    id: 7,
    title: "Monthly Revenue Report",
    difficulty: "Easy",
    slug: "sql-cte-common-table-expressions",
    seoTitle: "SQL CTE (Common Table Expressions) Tutorial",
    metaDescription: "Learn how to use SQL Common Table Expressions (CTEs) to calculate monthly revenue.",
  
    tags: ["SQL", "CTE", "WITH Clause", "GROUP BY"],
  
    description:
      "Use a Common Table Expression (CTE) to calculate the total revenue for each month.",
  
    explanation:
      "A Common Table Expression (CTE) created with the WITH clause allows you to define a temporary result set that can be referenced immediately in the main query. Here, the CTE calculates the total revenue for each month before returning the results.",
  
    scenario:
      "The finance team needs a monthly revenue report to monitor business performance and identify seasonal trends.",
  
    useCases: [
      "Monthly revenue reporting",
      "Financial dashboards",
      "Seasonal sales analysis"
    ],
  
    hint:
      "Create a CTE that groups orders by month using strftime('%m', order_date), then query the CTE.",
  
    starterQuery: `WITH MonthlyRevenue AS (
      SELECT
          strftime('%m', order_date) AS month,
          SUM(total_amount) AS total_revenue
      FROM orders
      GROUP BY strftime('%m', order_date)
  )
  SELECT
      month,
      total_revenue
  FROM MonthlyRevenue
  ORDER BY month;`,
  
    expectedColumns: [
      "month",
      "total_revenue"
    ],
  
    solutionQuery: `WITH MonthlyRevenue AS (
      SELECT
          strftime('%m', order_date) AS month,
          SUM(total_amount) AS total_revenue
      FROM orders
      GROUP BY strftime('%m', order_date)
  )
  SELECT
      month,
      total_revenue
  FROM MonthlyRevenue
  ORDER BY month;`,
  },
  {
    id: 8,
    title: "The 'Never-Ordered' Customers",
    difficulty: "Easy",
    slug: "sql-where-not-in-subquery",
    seoTitle: "SQL WHERE NOT IN Subquery",
    metaDescription: "Learn how to exclude specific record sets by deploying an operational SQL WHERE NOT IN subquery architecture.",
    tags: ["SQL", "NOT IN", "Subquery"],
    description: "Find customers who exist in the customers table but have 0 records in the orders table. Use a Subquery.",
    explanation: "Using 'NOT IN' with a subquery is a logical way to find the 'Delta' between two datasets.",
    scenario: "The sales team wants to call customers who signed up but never actually bought a single thing ('Ghost' users).",
    useCases: ["Win-back campaigns", "Data cleaning", "Lead conversion tracking"],
    hint: "WHERE customer_id NOT IN (SELECT customer_id FROM orders)",
    starterQuery: "SELECT customer_name, email \nFROM customers \nWHERE customer_id NOT IN (SELECT customer_id FROM orders);",
    expectedColumns: ["customer_name", "email"],
    expectedRowCount: 1,
    solutionQuery: "SELECT customer_name, email FROM customers WHERE customer_id NOT IN (SELECT customer_id FROM orders);",
  },
  {
    id: 9,
    title: "Percent of Total Revenue",
    difficulty: "Easy",
    slug: "sql-percentage-of-total-calculation",
    seoTitle: "SQL Percentage of Total Calculation",
    metaDescription: "Learn how to calculate each order's percentage contribution to the total revenue using SQL window functions.",
  
    tags: ["SQL", "Window Functions", "SUM OVER", "Percentage"],
  
    description:
      "For each order, display its total amount and the percentage it contributes to the overall revenue.",
  
    explanation:
      "Window functions can calculate values across all rows without grouping them. Divide each order's total_amount by the overall SUM(total_amount) OVER() to calculate its percentage contribution to the total revenue.",
  
    scenario:
      "The finance team wants to identify how much each order contributes to the company's overall revenue and determine whether revenue is concentrated in a few large orders.",
  
    useCases: [
      "Revenue contribution analysis",
      "Sales distribution",
      "Pareto (80/20) analysis"
    ],
  
    hint:
      "Calculate (total_amount * 100.0) / SUM(total_amount) OVER() to find the percentage contribution of each order.",
  
    starterQuery: `SELECT
      order_id,
      total_amount,
      (total_amount * 100.0 / SUM(total_amount) OVER()) AS pct_of_total
  FROM orders;`,
  
    expectedColumns: [
      "order_id",
      "total_amount",
      "pct_of_total"
    ],
  
    solutionQuery: `SELECT
      order_id,
      total_amount,
      (total_amount * 100.0 / SUM(total_amount) OVER()) AS pct_of_total
  FROM orders;`,
  },
  {
    id: 10,
    title: "Customer Signup to First Order (Days)",
    difficulty: "Easy",
    slug: "sql-date-difference-julianday",
    seoTitle: "SQL Date Difference Calculation",
    metaDescription: "Learn how to calculate the number of days between customer signup and their first order using SQLite date functions.",
  
    tags: ["SQL", "Date Functions", "julianday", "MIN"],
  
    description:
      "Calculate the number of days between a customer's account creation date and their first order date.",
  
    explanation:
      "Use the MIN() function to find each customer's first order date. Then use julianday() to calculate the difference in days between the customer's created_date and their first order.",
  
    scenario:
      "The marketing team wants to measure how quickly new customers place their first order after creating an account.",
  
    useCases: [
      "Customer onboarding analysis",
      "Conversion funnel reporting",
      "User behavior analytics"
    ],
  
    hint:
      "Join customers and orders, use MIN(order_date), and calculate julianday(MIN(order_date)) - julianday(created_date).",
  
    starterQuery: `SELECT
      c.customer_name,
      julianday(MIN(o.order_date)) - julianday(c.created_date) AS days_to_convert
  FROM customers c
  JOIN orders o
      ON c.customer_id = o.customer_id
  GROUP BY
      c.customer_id,
      c.customer_name,
      c.created_date;`,
  
    expectedColumns: [
      "customer_name",
      "days_to_convert"
    ],
  
    solutionQuery: `SELECT
      c.customer_name,
      julianday(MIN(o.order_date)) - julianday(c.created_date) AS days_to_convert
  FROM customers c
  JOIN orders o
      ON c.customer_id = o.customer_id
  GROUP BY
      c.customer_id,
      c.customer_name,
      c.created_date;`,
  },
  {
    id: 11,
    title: "Delivered Orders from Verified Customers",
    difficulty: "Easy",
    slug: "sql-multiple-where-conditions",
    seoTitle: "SQL Multiple WHERE Conditions",
    metaDescription: "Learn how to filter data using multiple conditions with the SQL WHERE clause.",
  
    tags: ["SQL", "WHERE Clause", "Filtering", "AND"],
  
    description: "Find all delivered orders placed by verified customers.",
  
    explanation: "Multiple conditions can be combined using the AND operator. Join the customers and orders tables, then filter for verified customers whose orders have been delivered.",
  
    scenario: "The customer success team wants to review completed purchases made only by verified customers.",
  
    useCases: [
      "Customer verification",
      "Order auditing",
      "Business reporting"
    ],
  
    hint: "Join customers and orders, then filter using is_verified and order_status.",
  
    starterQuery: `SELECT
      c.customer_name,
      o.order_id,
      o.order_date
  FROM customers c
  JOIN orders o
      ON c.customer_id = o.customer_id
  WHERE
      c.is_verified = 'true'
      AND o.order_status = 'delivered';`,
  
    expectedColumns: [
      "customer_name",
      "order_id",
      "order_date"
    ],
  
    solutionQuery: `SELECT
      c.customer_name,
      o.order_id,
      o.order_date
  FROM customers c
  JOIN orders o
      ON c.customer_id = o.customer_id
  WHERE
      c.is_verified = 'true'
      AND o.order_status = 'delivered';`,
  },
  {
    id: 12,
    title: "Average Order Value per Customer",
    difficulty: "Easy",
    slug: "sql-avg-group-by",
    seoTitle: "SQL AVG Aggregate Function with GROUP BY",
    metaDescription: "Learn how to compute group-level numeric averages by passing target key declarations into SQL AVG configurations.",
    tags: ["SQL", "AVG", "GROUP BY"],
    description: "Calculate the average order value for each customer.",
    explanation: "AVG aggregation grouped by customer gives spending behavior insights.",
    scenario: "Finance team wants to understand customer spending patterns.",
    useCases: ["Customer segmentation", "Revenue optimization"],
    hint: "GROUP BY customer_id and use AVG(total_amount)",
    starterQuery: "SELECT customer_id, AVG(total_amount) as avg_order_value \nFROM orders \nGROUP BY customer_id;",
    expectedColumns: ["customer_id", "avg_order_value"],
    expectedRowCount: 5,
    solutionQuery: "SELECT customer_id, AVG(total_amount) AS avg_order_value FROM orders GROUP BY customer_id;",
  },
  {
    id: 13,
    title: "Top Selling Category by Revenue",
    difficulty: "Easy",
    slug: "sql-sum-join-order-by-limit",
    seoTitle: "SQL JOIN with SUM, ORDER BY, and LIMIT",
    metaDescription: "Learn how to combine SQL JOIN, SUM accumulation, sorting arrays, and row boundaries to isolate top records.",
    tags: ["SQL", "SUM", "LIMIT Clause"],
    description: "Find which product category generates the highest total revenue.",
    explanation: "Requires joining order_items with products and aggregating revenue.",
    scenario: "Business wants to double down on the most profitable category.",
    useCases: ["Product strategy", "Revenue analysis"],
    hint: "JOIN order_items and products, SUM(total_price)",
    starterQuery: "SELECT p.category, SUM(oi.total_price) as revenue \nFROM order_items oi \nJOIN products p ON oi.product_id = p.product_id \nGROUP BY p.category \nORDER BY revenue DESC LIMIT 1;",
    expectedColumns: ["category", "revenue"],
    expectedRowCount: 1,
    solutionQuery: "SELECT p.category, SUM(oi.total_price) AS revenue FROM order_items oi JOIN products p ON oi.product_id = p.product_id GROUP BY p.category ORDER BY revenue DESC LIMIT 1;",
  },
  {
    id: 14,
    title: "Orders with Multiple Items",
    difficulty: "Easy",
    slug: "sql-count-group-by-having",
    seoTitle: "SQL COUNT Function with HAVING Clause",
    metaDescription: "Learn how to apply conditional size boundaries onto structural data categories using target HAVING setups.",
    tags: ["SQL", "COUNT", "HAVING"],
    description: "Find orders that contain more than 1 item.",
    explanation: "Aggregation on order_items helps identify complex orders.",
    scenario: "Operations team wants to identify bulk orders for special handling.",
    useCases: ["Logistics optimization", "Bulk order detection"],
    hint: "GROUP BY order_id and COUNT(order_item_id)",
    starterQuery: "SELECT order_id, COUNT(item_id) as item_count \nFROM order_items \nGROUP BY order_id \nHAVING item_count > 1;",
    expectedColumns: ["order_id", "item_count"],
    expectedRowCount: 4,
    solutionQuery: "SELECT order_id, COUNT(item_id) AS item_count FROM order_items GROUP BY order_id HAVING COUNT(item_id) > 1;",
  },
  {
    id: 15,
    title: "Payment Success Rate",
    difficulty: "Easy",
    slug: "sql-conditional-aggregation-case-when",
    seoTitle: "SQL Conditional Aggregation Using CASE WHEN",
    metaDescription: "Learn how to build ratio calculations directly inside query statements using conditional CASE WHEN frameworks.",
    tags: ["SQL", "CASE WHEN", "Conditional Aggregation"],
    description: "Calculate the percentage of successful payments.",
    explanation: "Conditional aggregation allows calculating ratios within a dataset.",
    scenario: "Payments team wants to monitor transaction success rate.",
    useCases: ["Fraud monitoring", "Payment gateway performance"],
    hint: "COUNT CASE WHEN payment_status = 'Success'",
    starterQuery: "SELECT \n(COUNT(CASE WHEN payment_status = 'Success' THEN 1 END) * 100.0 / COUNT(*)) as success_rate \nFROM payments;",
    expectedColumns: ["success_rate"],
    expectedRowCount: 1,
    solutionQuery: "SELECT (COUNT(CASE WHEN payment_status = 'Success' THEN 1 END) * 100.0 / COUNT(*)) AS success_rate FROM payments;",
  },
  {
    id: 16,
    title: "Customers with Highest Refund Amount",
    difficulty: "Easy",
    slug: "sql-multiple-table-joins-aggregation",
    seoTitle: "SQL Multiple Table Joins and Aggregation",
    metaDescription: "Learn how to resolve deep entity schemas by joining three or more transactional dataset tables in SQL.",
    tags: ["SQL", "Joins", "SUM Aggregation"],
    description: "Find customers who have the highest total refund amount.",
    explanation: "Join payments → orders → customers and aggregate refund_amount.",
    scenario: "Finance team wants to identify high refund-risk customers.",
    useCases: ["Fraud detection", "Customer risk scoring"],
    hint: "SUM(refund_amount) grouped by customer",
    starterQuery: "SELECT c.customer_name, SUM(p.refund_amount) as total_refund \nFROM payments p \nJOIN orders o ON p.order_id = o.order_id \nJOIN customers c ON o.customer_id = c.customer_id \nGROUP BY c.customer_name \nORDER BY total_refund DESC;",
    expectedColumns: ["customer_name", "total_refund"],
    expectedRowCount: 5,
    solutionQuery: "SELECT c.customer_name, SUM(p.refund_amount) AS total_refund FROM payments p JOIN orders o ON p.order_id = o.order_id JOIN customers c ON o.customer_id = c.customer_id GROUP BY c.customer_id, c.customer_name ORDER BY total_refund DESC;",
  },
  {
    id: 17,
    title: "Inactive Delivery Partners",
    difficulty: "Easy",
    slug: "sql-date-comparison-operators",
    seoTitle: "SQL Date Comparison Operators",
    metaDescription: "Learn how to isolate non-active asset groups by comparing dynamic historical date thresholds inside WHERE filters.",
    tags: ["SQL", "DATE Function", "Inactivity Filtering"],
    description: "Find delivery partners who have not been active in the last 60 days.",
    explanation: "Date filtering helps identify inactive workforce.",
    scenario: "Operations team wants to re-engage inactive delivery partners.",
    useCases: ["Workforce management", "Reactivation campaigns"],
    hint: "Filter last_active_date",
    starterQuery: "SELECT partner_name \nFROM delivery_partners \nWHERE last_active_date < DATE('now','-60 days');",
    expectedColumns: ["partner_name"],
    expectedRowCount: 3,
    solutionQuery: "SELECT partner_name FROM delivery_partners WHERE last_active_date < DATE('now','-60 days');",
  },
  {
    id: 18,
    title: "Customer Order Frequency Ranking",
    difficulty: "Easy",
    slug: "sql-rank-over-aggregate-function",
    seoTitle: "SQL RANK() OVER Window Function with Aggregation",
    metaDescription: "Learn how to execute the analytic RANK window function directly over grouped data parameters.",
    tags: ["SQL", "RANK", "Window Functions"],
    description: "Rank customers based on number of orders placed.",
    explanation: "Window functions allow ranking without collapsing rows.",
    scenario: "Marketing wants leaderboard of most active customers.",
    useCases: ["Gamification", "Customer ranking"],
    hint: "Use RANK() OVER (ORDER BY COUNT(order_id) DESC)",
    starterQuery: "SELECT customer_id, COUNT(order_id) as total_orders, \nRANK() OVER (ORDER BY COUNT(order_id) DESC) as rank \nFROM orders \nGROUP BY customer_id;",
    expectedColumns: ["customer_id", "total_orders", "rank"],
    expectedRowCount: 5,
    solutionQuery: "SELECT customer_id, COUNT(order_id) AS total_orders, RANK() OVER (ORDER BY COUNT(order_id) DESC) AS rank FROM orders GROUP BY customer_id;",
  },
  {
    id: 19,
    title: "Delayed Deliveries Count",
    difficulty: "Easy",
    slug: "sql-timestamp-column-comparison",
    seoTitle: "SQL Timestamp Column Comparison",
    metaDescription: "Learn how to compare two distinct timestamp column values inside a basic SQL WHERE filter expression.",
    tags: ["SQL", "Timestamp Comparison", "WHERE Clause"],
    description: "Count number of orders delivered after estimated delivery time.",
    explanation: "Comparing timestamps helps track SLA performance.",
    scenario: "Logistics team wants to monitor late deliveries.",
    useCases: ["SLA tracking", "Operational efficiency"],
    hint: "Compare delivered_date > estimated_delivery_time",
    starterQuery: "SELECT COUNT(*) as delayed_orders \nFROM orders \nWHERE delivered_date > estimated_delivery_time;",
    expectedColumns: ["delayed_orders"],
    expectedRowCount: 1,
    solutionQuery: "SELECT COUNT(*) AS delayed_orders FROM orders WHERE delivered_date > estimated_delivery_time;",
  },
  {
    id: 20,
    title: "Most Used Payment Method",
    difficulty: "Easy",
    slug: "sql-group-by-count-order-by-limit",
    seoTitle: "SQL GROUP BY, COUNT, ORDER BY, and LIMIT",
    metaDescription: "Learn how to combine classification groups with row sorting and volume bounds to locate top distinct modes.",
    tags: ["SQL", "GROUP BY", "LIMIT Clause"],
    description: "Find the most frequently used payment method.",
    explanation: "GROUP BY with COUNT helps identify dominant categories.",
    scenario: "Business wants to prioritize integrations with popular payment methods.",
    useCases: ["Payment optimization", "UX improvements"],
    hint: "GROUP BY payment_method ORDER BY COUNT DESC",
    starterQuery: "SELECT payment_method, COUNT(*) as usage_count \nFROM payments \nGROUP BY payment_method \nORDER BY usage_count DESC LIMIT 1;",
    expectedColumns: ["payment_method", "usage_count"],
    expectedRowCount: 1,
    solutionQuery: "SELECT payment_method, COUNT(*) AS usage_count FROM payments GROUP BY payment_method ORDER BY usage_count DESC LIMIT 1;",
  },
  {
    id: 21,
    title: "Customers with Negative Feedback",
    difficulty: "Easy",
    slug: "sql-inner-join-with-where-clause",
    seoTitle: "SQL INNER JOIN with WHERE Clause",
    metaDescription: "Learn how to relate separate table components together and pass direct alphanumeric matching conditions.",
    tags: ["SQL", "INNER JOIN", "WHERE Clause"],
    description: "Find customers who gave rating less than 3.",
    explanation: "Joining feedback with customers helps identify dissatisfied customers and enables support teams to take corrective actions.",
    scenario: "Customer support teams want to proactively contact unhappy customers before they churn.",
    useCases: [
      "Customer support",
      "Churn prevention",
      "Customer satisfaction analysis"
    ],
    hint: "JOIN feedback and customers WHERE rating < 3",
    starterQuery: "SELECT c.customer_name, f.rating \nFROM feedback f \nJOIN customers c ON f.customer_id = c.customer_id \nWHERE f.rating < 3;",
    expectedColumns: ["customer_name", "rating"],
    expectedRowCount: 4,
    solutionQuery: "SELECT c.customer_name, f.rating FROM feedback f JOIN customers c ON f.customer_id = c.customer_id WHERE f.rating < 3;",
  },
  {
    id: 22,
    title: "Average Delivery Time",
    difficulty: "Easy",
    slug: "sql-avg-with-date-subtraction",
    seoTitle: "SQL AVG with Date Subtraction Functions",
    metaDescription: "Learn how to average out time intervals by combining mean computations with underlying date subtraction syntax.",
    tags: ["SQL", "AVG", "Date Arithmetic"],
    description: "Calculate average delivery time in days.",
    explanation: "Date difference helps measure delivery efficiency.",
    scenario: "Operations team monitors average delivery speed.",
    useCases: ["Logistics KPIs", "Customer satisfaction"],
    hint: "Use julianday(delivered_date) - julianday(order_date)",
    starterQuery: "SELECT AVG(julianday(delivered_date) - julianday(order_date)) as avg_delivery_days \nFROM orders;",
    expectedColumns: ["avg_delivery_days"],
    expectedRowCount: 1,
    solutionQuery: "SELECT AVG(julianday(delivered_date) - julianday(order_date)) AS avg_delivery_days FROM orders;",
  },
  {
    id: 23,
    title: "Products Never Ordered",
    difficulty: "Easy",
    slug: "sql-left-join-is-null",
    seoTitle: "SQL LEFT JOIN with IS NULL Filter",
    metaDescription: "Learn how to identify isolated or unmatched structural table indexes using left-side joins and target null conditions.",
    tags: ["SQL", "LEFT JOIN", "IS NULL"],
    description: "Find products that have never been ordered.",
    explanation: "LEFT JOIN helps identify missing relationships.",
    scenario: "Inventory team wants to remove dead stock.",
    useCases: ["Inventory cleanup", "Catalog optimization"],
    hint: "LEFT JOIN order_items and filter NULL",
    starterQuery: "SELECT p.product_name \nFROM products p \nLEFT JOIN order_items oi ON p.product_id = oi.product_id \nWHERE oi.product_id IS NULL;",
    expectedColumns: ["product_name"],
    expectedRowCount: 3,
    solutionQuery: "SELECT p.product_name FROM products p LEFT JOIN order_items oi ON p.product_id = oi.product_id WHERE oi.product_id IS NULL;",
  },
  {
    id: 24,
    title: "Repeat Purchase Gap",
    difficulty: "Easy",
    slug: "sql-lag-window-function",
    seoTitle: "SQL LAG() OVER Window Function Tutorial",
    metaDescription: "Learn how to query row states chronologically using the analytic window LAG function parameters.",
    tags: ["SQL", "LAG", "Window Functions"],
    description: "Find the time difference between consecutive orders for each customer.",
    explanation: "LAG function helps compare previous rows.",
    scenario: "Analyze how frequently customers return.",
    useCases: ["Retention analysis", "Customer behavior"],
    hint: "Use LAG(order_date) OVER (PARTITION BY customer_id)",
    starterQuery: "SELECT customer_id, order_date, \nLAG(order_date) OVER (PARTITION BY customer_id ORDER BY order_date) as prev_order_date \nFROM orders;",
    expectedColumns: ["customer_id", "order_date", "prev_order_date"],
    expectedRowCount: 6,
    solutionQuery: "SELECT customer_id, order_date, LAG(order_date) OVER (PARTITION BY customer_id ORDER BY order_date) AS prev_order_date FROM orders;",
  },
  {
    id: 25,
    title: "High Discount Orders",
    difficulty: "Easy",
    slug: "sql-mathematical-operators-in-where",
    seoTitle: "SQL Mathematical Operators in WHERE Clause",
    metaDescription: "Learn how to execute numeric ratio math conditions inside relational query data filters.",
    tags: ["SQL", "Math Operators", "WHERE Filter"],
    description: "Find orders where discount exceeds 20% of subtotal.",
    explanation: "Ratio-based filtering helps identify aggressive promotions.",
    scenario: "Finance wants to track heavy discounting impact.",
    useCases: ["Discount analysis", "Profitability checks"],
    hint: "discount_amount / subtotal_amount > 0.2",
    starterQuery: "SELECT order_id \nFROM orders \nWHERE discount_amount * 1.0 / subtotal_amount > 0.2;",
    expectedColumns: ["order_id"],
    expectedRowCount: 2,
    solutionQuery: "SELECT order_id FROM orders WHERE discount_amount * 1.0 / subtotal_amount > 0.2;",
  },
  {
    id: 26,
    title: "Orders Without Items",
    difficulty: "Easy",
    slug: "sql-outer-join-data-integrity-validation",
    seoTitle: "SQL LEFT JOIN Data Integrity Validation",
    metaDescription: "Learn how to locate orphaned tracking indexes across systems using unlinked outer-join procedures.",
    tags: ["SQL", "LEFT JOIN", "Data Validation"],
    description: "Find orders that do not have any corresponding records in order_items.",
    explanation: "LEFT JOIN helps identify missing relationships between tables.",
    scenario: "Data team suspects some orders were created but items were not recorded.",
    useCases: ["Data validation", "Integrity checks"],
    hint: "LEFT JOIN order_items and filter NULL",
    starterQuery: "SELECT o.order_id \nFROM orders o \nLEFT JOIN order_items oi ON o.order_id = oi.order_id \nWHERE oi.order_id IS NULL;",
    expectedColumns: ["order_id"],
    expectedRowCount: 2,
    solutionQuery: "SELECT o.order_id FROM orders o LEFT JOIN order_items oi ON o.order_id = oi.order_id WHERE oi.order_id IS NULL;",
  },
  {
    id: 27,
    title: "Total Items Sold per Product",
    difficulty: "Easy",
    slug: "sql-sum-group-by-product-id",
    seoTitle: "SQL SUM with GROUP BY Product ID",
    metaDescription: "Learn how to aggregate item totals by product identities using basic SQL grouping statements.",
    tags: ["SQL", "SUM", "GROUP BY"],
    description: "Calculate total quantity sold for each product.",
    explanation: "SUM aggregation on quantity grouped by product_id gives total sales volume.",
    scenario: "Product team wants to know which items sell the most.",
    useCases: ["Inventory planning", "Demand analysis"],
    hint: "SUM(quantity) GROUP BY product_id",
    starterQuery: "SELECT product_id, SUM(quantity) as total_sold \nFROM order_items \nGROUP BY product_id;",
    expectedColumns: ["product_id", "total_sold"],
    expectedRowCount: 5,
    solutionQuery: "SELECT product_id, SUM(quantity) AS total_sold FROM order_items GROUP BY product_id;",
  },
  {
    id: 28,
    title: "Customer Lifetime Orders Value",
    difficulty: "Easy",
    slug: "sql-sum-aggregation-customer-id",
    seoTitle: "SQL SUM Aggregation with Customer ID Grouping",
    metaDescription: "Learn how to summarize continuous ledger entries per customer profile via localized sum grouping.",
    tags: ["SQL", "SUM", "Customer Tracking"],
    description: "Calculate total order value per customer.",
    explanation: "Aggregating total_amount grouped by customer shows lifetime spend.",
    scenario: "Marketing wants to identify high-value customers.",
    useCases: ["LTV analysis", "Customer segmentation"],
    hint: "SUM(total_amount) GROUP BY customer_id",
    starterQuery: "SELECT customer_id, SUM(total_amount) as lifetime_spend \nFROM orders \nGROUP BY customer_id;",
    expectedColumns: ["customer_id", "lifetime_spend"],
    expectedRowCount: 5,
    solutionQuery: "SELECT customer_id, SUM(total_amount) AS lifetime_spend FROM orders GROUP BY customer_id;",
  },
  {
    id: 29,
    title: "Orders with Multiple Payment Attempts",
    difficulty: "Easy",
    slug: "sql-having-count-validation",
    seoTitle: "SQL HAVING COUNT Validation Queries",
    metaDescription: "Learn how to surface system transaction retry cycles using HAVING COUNT data evaluation logic.",
    tags: ["SQL", "COUNT", "HAVING"],
    description: "Find orders that have more than one payment attempt.",
    explanation: "Grouping payments by order_id helps detect retries.",
    scenario: "Payments team investigates unstable transactions.",
    useCases: ["Payment retries", "Failure tracking"],
    hint: "COUNT(payment_id) > 1",
    starterQuery: "SELECT order_id, COUNT(payment_id) as attempts \nFROM payments \nGROUP BY order_id \nHAVING attempts > 1;",
    expectedColumns: ["order_id", "attempts"],
    expectedRowCount: 3,
    solutionQuery: "SELECT order_id, COUNT(payment_id) AS attempts FROM payments GROUP BY order_id HAVING COUNT(payment_id) > 1;",
  },
  {
    id: 30,
    title: "Average Rating per Customer",
    difficulty: "Easy",
    slug: "sql-avg-group-by-customer-id",
    seoTitle: "SQL AVG with GROUP BY Customer ID",
    metaDescription: "Learn how to compute historical score trends by grouping numeric metrics under distinct user accounts.",
    tags: ["SQL", "AVG", "GROUP BY"],
    description: "Calculate average feedback rating for each customer.",
    explanation: "AVG aggregation grouped by customer_id shows satisfaction levels.",
    scenario: "Support team wants to identify happy vs unhappy customers.",
    useCases: ["Customer satisfaction", "NPS analysis"],
    hint: "AVG(rating) GROUP BY customer_id",
    starterQuery: "SELECT customer_id, AVG(rating) as avg_rating \nFROM feedback \nGROUP BY customer_id;",
    expectedColumns: ["customer_id", "avg_rating"],
    expectedRowCount: 5,
    solutionQuery: "SELECT customer_id, AVG(rating) AS avg_rating FROM feedback GROUP BY customer_id;",
  },
  {
    id: 31,
    title: "Orders Delivered Per Partner",
    difficulty: "Easy",
    slug: "sql-count-group-by-where-clause",
    seoTitle: "SQL COUNT Function with WHERE and GROUP BY",
    metaDescription: "Learn how to use the SQL COUNT function alongside WHERE filters and GROUP BY clauses to aggregate filtered datasets.",
    tags: ["SQL", "COUNT", "GROUP BY"],
    description: "Count number of delivered orders per delivery partner.",
    explanation: "Filtering delivered orders and grouping by partner shows workload distribution.",
    scenario: "Operations team tracks delivery partner performance.",
    useCases: ["Performance tracking", "Logistics optimization"],
    hint: "WHERE order_status = 'Delivered'",
    starterQuery: "SELECT delivery_partner_id, COUNT(*) as delivered_orders \nFROM orders \nWHERE order_status = 'delivered' \nGROUP BY delivery_partner_id;",
    expectedColumns: ["delivery_partner_id", "delivered_orders"],
    expectedRowCount: 4,
    solutionQuery: "SELECT delivery_partner_id, COUNT(*) AS delivered_orders FROM orders WHERE order_status = 'delivered' GROUP BY delivery_partner_id;",
  },
  {
    id: 32,
    title: "Revenue by City",
    difficulty: "Easy",
    slug: "sql-join-with-sum-group-by",
    seoTitle: "SQL JOIN with SUM and GROUP BY Clauses",
    metaDescription: "Learn how to combine relational tables using an INNER JOIN and calculate totals using SUM and GROUP BY statement properties.",
    tags: ["SQL", "JOIN", "SUM"],
    description: "Calculate total revenue generated from each customer city.",
    explanation: "Joining customers and orders allows geographic revenue breakdown.",
    scenario: "Expansion team analyzes top-performing cities.",
    useCases: ["Geo analysis", "Market expansion"],
    hint: "JOIN customers and orders",
    starterQuery: "SELECT c.city, SUM(o.total_amount) as revenue \nFROM customers c \nJOIN orders o ON c.customer_id = o.customer_id \nGROUP BY c.city;",
    expectedColumns: ["city", "revenue"],
    expectedRowCount: 5,
    solutionQuery: "SELECT c.city, SUM(o.total_amount) AS revenue FROM customers c JOIN orders o ON c.customer_id = o.customer_id GROUP BY c.city;",
  },
  {
    id: 33,
    title: "Active vs Inactive Products",
    difficulty: "Easy",
    slug: "sql-group-by-boolean-field",
    seoTitle: "SQL GROUP BY Boolean Column Condition",
    metaDescription: "Learn how to cluster data rows and compute operational counts using boolean true or false flag columns.",
    tags: ["SQL", "GROUP BY", "COUNT"],
    description: "Count number of active and inactive products.",
    explanation: "GROUP BY boolean field helps segment product status.",
    scenario: "Catalog team monitors product availability.",
    useCases: ["Inventory health", "Catalog audit"],
    hint: "GROUP BY is_active",
    starterQuery: "SELECT is_active, COUNT(*) as count \nFROM products \nGROUP BY is_active;",
    expectedColumns: ["is_active", "count"],
    expectedRowCount: 2,  
    solutionQuery: "SELECT is_active, COUNT(*) AS count FROM products GROUP BY is_active;",
  },
  {
    id: 34,
    title: "High Value Orders",
    difficulty: "Easy",
    slug: "sql-where-mathematical-filter",
    seoTitle: "SQL Mathematical Filters in WHERE Clause",
    metaDescription: "Learn how to filter rows using mathematical expressions in the SQL WHERE clause.",
  
    tags: ["SQL", "WHERE", "Math Operators"],
  
    description: "Find orders where the tax amount is greater than 40.",
  
    explanation: "The WHERE clause can filter rows using mathematical comparisons. This query returns only orders whose tax amount exceeds 40.",
  
    scenario: "The finance team wants to review high-tax transactions for auditing purposes.",
  
    useCases: [
      "Financial auditing",
      "Tax analysis",
      "Exception reporting"
    ],
  
    hint: "Filter rows using WHERE tax_amount > 40.",
  
    starterQuery: `SELECT
      order_id,
      subtotal_amount,
      tax_amount
  FROM orders
  WHERE tax_amount > 40;`,
  
    expectedColumns: [
      "order_id",
      "subtotal_amount",
      "tax_amount"
    ],
  
    solutionQuery: `SELECT
      order_id,
      subtotal_amount,
      tax_amount
  FROM orders
  WHERE tax_amount > 40;`,
  },
  {
    id: 35,
    title: "Customers with Only One Order",
    difficulty: "Easy",
    slug: "sql-having-count-equals-one",
    seoTitle: "SQL HAVING COUNT Equals Match Conditions",
    metaDescription: "Learn how to apply precise volumetric matching boundaries onto grouped identifiers using HAVING COUNT logic constraints.",
    tags: ["SQL", "HAVING", "COUNT"],
    description: "Find customers who placed exactly one order.",
    explanation: "HAVING COUNT = 1 filters exact occurrences.",
    scenario: "Marketing targets first-time buyers.",
    useCases: ["Onboarding campaigns", "Retention"],
    hint: "HAVING COUNT(order_id) = 1",
    starterQuery: "SELECT customer_id \nFROM orders \nGROUP BY customer_id \nHAVING COUNT(order_id) = 1;",
    expectedColumns: ["customer_id"],
    expectedRowCount: 3,
    solutionQuery: "SELECT customer_id FROM orders GROUP BY customer_id HAVING COUNT(order_id) = 1;",
  },
  {
    id: 36,
    title: "Top Rated Delivery Partners",
    difficulty: "Medium",
    slug: "sql-where-greater-than-subquery",
    seoTitle: "SQL WHERE Greater Than Subquery Selection",
    metaDescription: "Learn how to evaluate row-level field quantities against a dynamic nested scalar average calculation subquery block.",
    tags: ["SQL", "Subquery", "AVG"],
    description: "Find delivery partners with rating above average.",
    explanation: "Subquery calculates average rating for comparison.",
    scenario: "Operations wants to reward best performers.",
    useCases: ["Performance incentives", "Ranking"],
    hint: "rating > (SELECT AVG(rating) FROM delivery_partners)",
    starterQuery: "SELECT partner_name \nFROM delivery_partners \nWHERE rating > (SELECT AVG(rating) FROM delivery_partners);",
    expectedColumns: ["partner_name"],
    expectedRowCount: 3,
    solutionQuery: "SELECT partner_name FROM delivery_partners WHERE rating > (SELECT AVG(rating) FROM delivery_partners);",
  },
  {
    id: 37,
    title: "Orders Cancelled per Reason",
    difficulty: "Medium",
    slug: "sql-group-by-is-not-null",
    seoTitle: "SQL GROUP BY with IS NOT NULL Filter",
    metaDescription: "Learn how to drop null entries using target row filters prior to aggregating and grouping categorical data elements.",
    tags: ["SQL", "GROUP BY", "IS NOT NULL"],
    description: "Count number of orders grouped by cancellation reason.",
    explanation: "Grouping categorical data helps identify major issues.",
    scenario: "Product team wants to reduce cancellations.",
    useCases: ["Root cause analysis", "Process improvement"],
    hint: "GROUP BY cancellation_reason",
    starterQuery: "SELECT cancellation_reason, COUNT(*) as count \nFROM orders \nWHERE cancellation_reason IS NOT NULL \nGROUP BY cancellation_reason;",
    expectedColumns: ["cancellation_reason", "count"],
    expectedRowCount: 4,
    solutionQuery: "SELECT cancellation_reason, COUNT(*) AS count FROM orders WHERE cancellation_reason IS NOT NULL GROUP BY cancellation_reason;",
  },
  {
    id: 38,
    title: "Revenue Contribution per Customer",
    difficulty: "Medium",
    slug: "sql-window-sum-over-nested-aggregation",
    seoTitle: "SQL Window SUM OVER Function with Nested Aggregation",
    metaDescription: "Learn how to compute relational ratios by dividing standard column sums against an overarching window grand total metric.",
    tags: ["SQL", "Window Functions", "SUM OVER"],
    description: "Calculate each customer's contribution to total revenue.",
    explanation: "Divide customer revenue by total revenue using window functions.",
    scenario: "Business evaluates dependency on top customers.",
    useCases: ["Revenue share", "Risk analysis"],
    hint: "SUM(total_amount) OVER()",
    starterQuery: "SELECT customer_id, SUM(total_amount) as total, \nSUM(total_amount) * 100.0 / SUM(SUM(total_amount)) OVER() as pct \nFROM orders \nGROUP BY customer_id;",
    expectedColumns: ["customer_id", "total", "pct"],
    expectedRowCount: 5,
    solutionQuery: "SELECT customer_id, SUM(total_amount) AS total, SUM(total_amount) * 100.0 / SUM(SUM(total_amount)) OVER() AS pct FROM orders GROUP BY customer_id;",
  },
  {
    id: 39,
    title: "Products Above Average Price",
    difficulty: "Medium",
    slug: "sql-scalar-subquery-with-where",
    seoTitle: "SQL Scalar Subquery Configuration within WHERE Filters",
    metaDescription: "Learn how to pull a structural baseline index value from a nested select and execute comparison query checks.",
    tags: ["SQL", "Subquery", "WHERE Clause"],
    description: "Find products priced above average.",
    explanation: "Scalar subquery compares each row to overall average.",
    scenario: "Pricing team identifies premium products.",
    useCases: ["Pricing strategy", "Product segmentation"],
    hint: "price > (SELECT AVG(price) FROM products)",
    starterQuery: "SELECT product_name, price \nFROM products \nWHERE price > (SELECT AVG(price) FROM products);",
    expectedColumns: ["product_name", "price"],
    expectedRowCount: 3,
    solutionQuery: "SELECT product_name, price FROM products WHERE price > (SELECT AVG(price) FROM products);",
  },
  {
    id: 40,
    title: "Orders per Day",
    difficulty: "Medium",
    slug: "sql-group-by-date-column",
    seoTitle: "SQL GROUP BY Date Statement Formatting",
    metaDescription: "Learn how to execute quantitative metrics sorting over specific chronology limits using simple GROUP BY date column operations.",
    tags: ["SQL", "GROUP BY", "Date Analytics"],
    description: "Count number of orders placed each day.",
    explanation: "Grouping by date helps analyze daily trends.",
    scenario: "Analytics team tracks daily activity.",
    useCases: ["Trend analysis", "Dashboard metrics"],
    hint: "GROUP BY order_date",
    starterQuery: "SELECT order_date, COUNT(*) as total_orders \nFROM orders \nGROUP BY order_date;",
    expectedColumns: ["order_date", "total_orders"],
    expectedRowCount: 6,
    solutionQuery: "SELECT order_date, COUNT(*) AS total_orders FROM orders GROUP BY order_date;",
  },
  {
    id: 41,
    title: "Customers Without Feedback",
    difficulty: "Medium",
    slug: "sql-left-join-identity-is-null",
    seoTitle: "SQL LEFT JOIN with Identifier IS NULL Constraint",
    metaDescription: "Learn how to parse unmatched key fields across related data spaces using left-sided joins and terminal null validation parameters.",
    tags: ["SQL", "LEFT JOIN", "IS NULL"],
    description: "Find customers who never gave feedback.",
    explanation: "LEFT JOIN helps find missing feedback entries.",
    scenario: "Product team wants more reviews.",
    useCases: ["Feedback campaigns", "User engagement"],
    hint: "LEFT JOIN feedback",
    starterQuery: "SELECT c.customer_name \nFROM customers c \nLEFT JOIN feedback f ON c.customer_id = f.customer_id \nWHERE f.customer_id IS NULL;",
    expectedColumns: ["customer_name"],
    expectedRowCount: 2,
    solutionQuery: "SELECT c.customer_name FROM customers c LEFT JOIN feedback f ON c.customer_id = f.customer_id WHERE f.customer_id IS NULL;",
  },
  {
    id: 42,
    title: "Highest Order per Day",
    difficulty: "Medium",
    slug: "sql-max-aggregate-with-group-by-date",
    seoTitle: "SQL MAX Aggregation with GROUP BY Date",
    metaDescription: "Learn how to output topmost column metrics inside discrete chronological buckets by merging MAX queries with date groupings.",
    tags: ["SQL", "MAX", "GROUP BY"],
    description: "Find the highest order value for each day.",
    explanation: "MAX aggregation grouped by date.",
    scenario: "Finance tracks peak transactions daily.",
    useCases: ["Peak analysis", "Daily insights"],
    hint: "MAX(total_amount)",
    starterQuery: "SELECT order_date, MAX(total_amount) as max_order \nFROM orders \nGROUP BY order_date;",
    expectedColumns: ["order_date", "max_order"],
    expectedRowCount: 6,
    solutionQuery: "SELECT order_date, MAX(total_amount) AS max_order FROM orders GROUP BY order_date;",
  },
  {
    id: 43,
    title: "Payment Failures per Provider",
    difficulty: "Medium",
    slug: "sql-count-where-string-match",
    seoTitle: "SQL COUNT with WHERE String Character Matching",
    metaDescription: "Learn how to isolate target status strings using specific where checks and summarize total volumes per system provider.",
    tags: ["SQL", "COUNT", "WHERE Clause"],
    description: "Count failed payments grouped by provider.",
    explanation: "Filtering failed payments and grouping shows provider issues.",
    scenario: "Tech team monitors payment gateway failures.",
    useCases: ["Gateway analysis", "Reliability tracking"],
    hint: "WHERE payment_status = 'Failed'",
    starterQuery: "SELECT payment_provider, COUNT(*) as failures \nFROM payments \nWHERE payment_status = 'failed' \nGROUP BY payment_provider;",
    expectedColumns: ["payment_provider", "failures"],
    expectedRowCount: 3,
    solutionQuery: "SELECT payment_provider, COUNT(*) AS failures FROM payments WHERE payment_status = 'failed' GROUP BY payment_provider;",
  },
  {
    id: 44,
    title: "Orders with No Discount",
    difficulty: "Medium",
    slug: "sql-where-equals-zero-numeric-filter",
    seoTitle: "SQL WHERE Equals Zero Numeric Filters",
    metaDescription: "Learn how to execute literal numeric evaluations inside a baseline filter statement to map rows with zeroed properties.",
    tags: ["SQL", "WHERE Clause", "Numeric Filtering"],
    description: "Find orders where no discount was applied.",
    explanation: "Simple filtering based on discount_amount.",
    scenario: "Marketing evaluates discount penetration.",
    useCases: ["Promotion analysis", "Revenue quality"],
    hint: "discount_amount = 0",
    starterQuery: "SELECT order_id \nFROM orders \nWHERE discount_amount = 0;",
    expectedColumns: ["order_id"],
    expectedRowCount: 4,
    solutionQuery: "SELECT order_id FROM orders WHERE discount_amount = 0;",
  },
  {
    id: 45,
    title: "Top Customers by Order Count",
    difficulty: "Medium",
    slug: "sql-group-by-order-by-count-limit",
    seoTitle: "SQL GROUP BY and ORDER BY COUNT with LIMIT Bounds",
    metaDescription: "Learn how to cluster category keys, rank them sorted by cumulative volume, and bound row counts using LIMIT syntax expressions.",
    tags: ["SQL", "ORDER BY", "LIMIT Clause"],
    description: "Find top 3 customers by number of orders.",
    explanation: "Grouping and ordering helps rank customers.",
    scenario: "Business identifies most active users.",
    useCases: ["Customer ranking", "Retention strategy"],
    hint: "ORDER BY COUNT DESC LIMIT 3",
    starterQuery: "SELECT customer_id, COUNT(*) as total_orders \nFROM orders \nGROUP BY customer_id \nORDER BY total_orders DESC LIMIT 3;",
    expectedColumns: ["customer_id", "total_orders"],
    expectedRowCount: 3,
    solutionQuery: "SELECT customer_id, COUNT(*) AS total_orders FROM orders GROUP BY customer_id ORDER BY total_orders DESC LIMIT 3;",
  },
  {
    id: 46,
    title: "Revenue per Product",
    difficulty: "Medium",
    slug: "sql-inner-join-sum-group-by-string",
    seoTitle: "SQL INNER JOIN with SUM and GROUP BY String Keys",
    metaDescription: "Learn how to compile revenue metrics across separate entity systems by mapping key indexes and grouping data strings.",
    tags: ["SQL", "INNER JOIN", "SUM"],
    description: "Calculate revenue generated by each product.",
    explanation: "SUM(total_price) grouped by product_id.",
    scenario: "Product team evaluates profitability.",
    useCases: ["Revenue tracking", "Product performance"],
    hint: "JOIN products and order_items",
    starterQuery: "SELECT p.product_name, SUM(oi.total_price) as revenue \nFROM order_items oi \nJOIN products p ON oi.product_id = p.product_id \nGROUP BY p.product_name;",
    expectedColumns: ["product_name", "revenue"],
    expectedRowCount: 5,
    solutionQuery: "SELECT p.product_name, SUM(oi.total_price) AS revenue FROM order_items oi JOIN products p ON oi.product_id = p.product_id GROUP BY p.product_name;",
  },
  {
    id: 47,
    title: "Customers with High Lifetime Value",
    difficulty: "Medium",
    slug: "sql-subquery-with-avg-comparison",
    seoTitle: "SQL Subquery with AVG Function Comparisons",
    metaDescription: "Learn how to contrast table metrics against an isolated column-wide average calculated inside a nested subquery parameter.",
    tags: ["SQL", "Subquery", "AVG"],
    description: "Find customers whose lifetime_value is above average.",
    explanation: "Scalar subquery compares values against average.",
    scenario: "Marketing targets premium users.",
    useCases: ["VIP segmentation", "High-value targeting"],
    hint: "lifetime_value > AVG(lifetime_value)",
    starterQuery: "SELECT customer_name \nFROM customers \nWHERE lifetime_value > (SELECT AVG(lifetime_value) FROM customers);",
    expectedColumns: ["customer_name"],
    expectedRowCount: 3,
    solutionQuery: "SELECT customer_name FROM customers WHERE lifetime_value > (SELECT AVG(lifetime_value) FROM customers);",
  },
  {
    id: 48,
    title: "Orders by Payment Status",
    difficulty: "Medium",
    slug: "sql-count-group-by-status-string",
    seoTitle: "SQL COUNT with GROUP BY Category Status String",
    metaDescription: "Learn how to separate system datasets into structural buckets and count entries using string category parameters.",
    tags: ["SQL", "COUNT", "GROUP BY"],
    description: "Count number of orders grouped by payment_status.",
    explanation: "Grouping helps understand payment distribution.",
    scenario: "Finance tracks successful vs pending payments.",
    useCases: ["Payment monitoring", "Business KPIs"],
    hint: "GROUP BY payment_status",
    starterQuery: "SELECT payment_status, COUNT(*) as count \nFROM orders \nGROUP BY payment_status;",
    expectedColumns: ["payment_status", "count"],
    expectedRowCount: 3,
    solutionQuery: "SELECT payment_status, COUNT(*) AS count FROM orders GROUP BY payment_status;",
  },
  {
    id: 49,
    title: "Products per Category Count",
    difficulty: "Medium",
    slug: "sql-group-by-categorical-column",
    seoTitle: "SQL GROUP BY Categorical Column Fields",
    metaDescription: "Learn how to parse an asset catalog space and aggregate volume distributions across primary database item categories.",
    tags: ["SQL", "GROUP BY", "COUNT"],
    description: "Count number of products in each category.",
    explanation: "Grouping products by category gives catalog distribution.",
    scenario: "Catalog team analyzes product spread.",
    useCases: ["Inventory planning", "Category balance"],
    hint: "GROUP BY category",
    starterQuery: "SELECT category, COUNT(*) as product_count \nFROM products \nGROUP BY category;",
    expectedColumns: ["category", "product_count"],
    expectedRowCount: 4,
    solutionQuery: "SELECT category, COUNT(*) AS product_count FROM products GROUP BY category;",
  },
  {
    id: 50,
    title: "Customers with Verified Accounts Only",
    difficulty: "Medium",
    slug: "sql-where-clause-multiple-and-conditions",
    seoTitle: "SQL WHERE Clause Multiple AND Conditions",
    metaDescription: "Learn how to refine record lookups by joining separate evaluation terms using string and boolean AND operator checks.",
    tags: ["SQL", "WHERE Clause", "AND Operator"],
    description: "Find all customers who are verified and active.",
    explanation: "Filtering on multiple conditions refines dataset.",
    scenario: "Security team wants only trusted users.",
    useCases: ["User filtering", "Security checks"],
    hint: "is_verified = true AND status = 'Active'",
    starterQuery: "SELECT customer_name \nFROM customers \nWHERE is_verified = 'true' AND status = 'active';",
    expectedColumns: ["customer_name"],
    expectedRowCount: 4,
    solutionQuery: "SELECT customer_name FROM customers WHERE is_verified = 'true' AND status = 'active';",
  },
  {
    id: 51,
    title: "Total Payments by Payment Method",
    difficulty: "Medium",
    slug: "sql-group-by-sum-payments",
    seoTitle: "SQL GROUP BY with SUM Function",
    metaDescription: "Learn how to group payment records and calculate total payment amount for each payment method using SQL SUM().",
    tags: ["SQL", "GROUP BY", "SUM"],
    description: "Calculate the total payment amount collected for each payment method.",
    explanation: "GROUP BY creates one group for each payment method, while SUM() calculates the total payment amount collected for that method.",
    scenario: "The finance team wants to know which payment methods contribute the most revenue.",
    useCases: ["Revenue reporting", "Payment analytics", "Business dashboards"],
    hint: "GROUP BY payment_method and use SUM(amount).",
    starterQuery: "SELECT payment_method, SUM(amount) AS total_amount \nFROM payments \nGROUP BY payment_method \nORDER BY total_amount DESC;",
    expectedColumns: ["payment_method", "total_amount"],
    solutionQuery: "SELECT payment_method, SUM(amount) AS total_amount FROM payments GROUP BY payment_method ORDER BY total_amount DESC;",
},
  {
    id: 52,
    title: "Customers with Orders in Multiple Cities",
    difficulty: "Medium",
    slug: "sql-having-count-distinct-condition",
    seoTitle: "SQL HAVING COUNT DISTINCT Evaluation Queries",
    metaDescription: "Learn how to check for broad geo-spatial coverage by evaluating distinct item variety values within a HAVING statement.",
    tags: ["SQL", "COUNT DISTINCT", "HAVING Clause"],
    description: "Find customers who have placed orders delivered in more than one city.",
    explanation: "Joining delivery partners enables tracking geographic spread of deliveries.",
    scenario: "Logistics team wants to identify customers ordering across locations.",
    useCases: ["Geo behavior analysis", "Expansion insights"],
    hint: "COUNT(DISTINCT delivery_partners.city)",
    starterQuery: "SELECT o.customer_id \nFROM orders o \nJOIN delivery_partners d ON o.delivery_partner_id = d.delivery_partner_id \nGROUP BY o.customer_id \nHAVING COUNT(DISTINCT d.city) > 1;",
    expectedColumns: ["customer_id"],
    expectedRowCount: 3,
    solutionQuery: "SELECT o.customer_id FROM orders o JOIN delivery_partners d ON o.delivery_partner_id = d.delivery_partner_id GROUP BY o.customer_id HAVING COUNT(DISTINCT d.city) > 1;",
  },
  {
    id: 53,
    title: "Average Items per Order",
    difficulty: "Medium",
    slug: "sql-avg-from-nested-subquery-count",
    seoTitle: "SQL AVG Aggregate from Nested Subquery Count",
    metaDescription: "Learn how to derive overarching system averages by nesting a quantitative count query block inside a main select statement.",
    tags: ["SQL", "Subquery", "AVG Function"],
    description: "Calculate average number of items per order.",
    explanation: "Combining COUNT and AVG across grouped data provides order complexity insight.",
    scenario: "Operations wants to estimate average packing effort.",
    useCases: ["Warehouse planning", "Fulfillment optimization"],
    hint: "COUNT(order_item_id) per order, then AVG",
    starterQuery: "SELECT AVG(item_count) as avg_items \nFROM (SELECT order_id, COUNT(*) as item_count FROM order_items GROUP BY order_id);",
    expectedColumns: ["avg_items"],
    expectedRowCount: 1,
    solutionQuery: "SELECT AVG(item_count) AS avg_items FROM (SELECT order_id, COUNT(*) AS item_count FROM order_items GROUP BY order_id);",
  },
  {
    id: 54,
    title: "Customers with Declining Order Value",
    difficulty: "Medium",
    slug: "sql-lag-window-function-subquery-comparison",
    seoTitle: "SQL LAG Window Function with Derived Subquery Comparison",
    metaDescription: "Learn how to leverage individual timeline row evaluations by embedding analytical LAG functions inside subquery components.",
    tags: ["SQL", "LAG Function", "Window Functions"],
    description: "Find customers whose latest order value is less than their previous order.",
    explanation: "LAG helps compare consecutive transactions.",
    scenario: "Business wants to detect declining spending behavior.",
    useCases: ["Churn prediction", "Behavior analysis"],
    hint: "Use LAG(total_amount)",
    starterQuery: "SELECT customer_id \nFROM (SELECT customer_id, total_amount, LAG(total_amount) OVER (PARTITION BY customer_id ORDER BY order_date) as prev_amt FROM orders) t \nWHERE total_amount < prev_amt;",
    expectedColumns: ["customer_id"],
    expectedRowCount: 3,
    solutionQuery: "SELECT customer_id FROM (SELECT customer_id, total_amount, LAG(total_amount) OVER (PARTITION BY customer_id ORDER BY order_date) AS prev_amt FROM orders) t WHERE total_amount < prev_amt;",
  },
  {
    id: 55,
    title: "Orders with Highest Item Value",
    difficulty: "Medium",
    slug: "sql-max-group-by-order-id",
    seoTitle: "SQL MAX Function with Order ID Grouping",
    metaDescription: "Learn how to isolate peak values within discrete purchase records by combining MAX tools with explicit order item keys.",
    tags: ["SQL", "MAX", "GROUP BY"],
    description: "Find the highest total_price item in each order.",
    explanation: "MAX aggregation identifies the most expensive item per order.",
    scenario: "Analyze premium product contribution in orders.",
    useCases: ["Upsell analysis", "Product insights"],
    hint: "MAX(total_price) GROUP BY order_id",
    starterQuery: "SELECT order_id, MAX(total_price) as max_item \nFROM order_items \nGROUP BY order_id;",
    expectedColumns: ["order_id", "max_item"],
    expectedRowCount: 5,
    solutionQuery: "SELECT order_id, MAX(total_price) AS max_item FROM order_items GROUP BY order_id;",
  },
  {
    id: 56,
    title: "Delivery Partner Workload Share",
    difficulty: "Medium",
    slug: "sql-window-count-percentage-ratio",
    seoTitle: "SQL Window COUNT Partition and Percentage Ratio Calculations",
    metaDescription: "Learn how to compute group-level distribution weight metrics by dividing active category counts against window grand totals.",
    tags: ["SQL", "Window Functions", "COUNT OVER"],
    description: "Calculate percentage of total deliveries handled by each partner.",
    explanation: "Window functions allow computing percentage share.",
    scenario: "Operations wants to balance delivery workload.",
    useCases: ["Workload distribution", "Performance tracking"],
    hint: "COUNT(*) * 100 / SUM(COUNT(*)) OVER()",
    starterQuery: "SELECT delivery_partner_id, COUNT(*) as total_orders, \nCOUNT(*) * 100.0 / SUM(COUNT(*)) OVER() as pct_share \nFROM orders \nGROUP BY delivery_partner_id;",
    expectedColumns: ["delivery_partner_id", "total_orders", "pct_share"],
    expectedRowCount: 4,
    solutionQuery: "SELECT delivery_partner_id, COUNT(*) AS total_orders, COUNT(*) * 100.0 / SUM(COUNT(*)) OVER() AS pct_share FROM orders GROUP BY delivery_partner_id;",
  },
  {
    id: 57,
    title: "Products with Low Sales Volume",
    difficulty: "Medium",
    slug: "sql-having-sum-less-than-subquery-avg",
    seoTitle: "SQL HAVING SUM Less Than Subquery AVG Bounds",
    metaDescription: "Learn how to drop poor performers by evaluating group metrics against a global mean derived inside a nested subquery.",
    tags: ["SQL", "HAVING Clause", "Nested Subquery"],
    description: "Find products with total quantity sold below average.",
    explanation: "Compare grouped sums with overall average.",
    scenario: "Inventory team identifies underperforming products.",
    useCases: ["Product pruning", "Inventory control"],
    hint: "HAVING SUM(quantity) < (SELECT AVG(...))",
    starterQuery: "SELECT product_id, SUM(quantity) as total_qty \nFROM order_items \nGROUP BY product_id \nHAVING total_qty < (SELECT AVG(qty) FROM (SELECT SUM(quantity) as qty FROM order_items GROUP BY product_id));",
    expectedColumns: ["product_id", "total_qty"],
    expectedRowCount: 3,
    solutionQuery: "SELECT product_id, SUM(quantity) AS total_qty FROM order_items GROUP BY product_id HAVING SUM(quantity) < (SELECT AVG(qty) FROM (SELECT SUM(quantity) AS qty FROM order_items GROUP BY product_id));",
  },
  {
    id: 58,
    title: "Customers Ordering After Long Gap",
    difficulty: "Medium",
    slug: "sql-julianday-date-difference-lag-window",
    seoTitle: "SQL julianday Date Difference with LAG Window Function",
    metaDescription: "Learn how to parse chronological distance markers by executing julianday date arithmetic on structural LAG column indexes.",
    tags: ["SQL", "julianday", "LAG Function"],
    description: "Find customers who placed orders after a gap of more than 60 days.",
    explanation: "Date difference using LAG helps detect inactive periods.",
    scenario: "Marketing targets reactivated customers.",
    useCases: ["Reactivation campaigns", "User retention"],
    hint: "julianday difference > 60",
    starterQuery: "SELECT customer_id \nFROM (SELECT customer_id, order_date, LAG(order_date) OVER (PARTITION BY customer_id ORDER BY order_date) as prev_date FROM orders) t \nWHERE julianday(order_date) - julianday(prev_date) > 60;",
    expectedColumns: ["customer_id"],
    expectedRowCount: 3,
    solutionQuery: "SELECT customer_id FROM (SELECT customer_id, order_date, LAG(order_date) OVER (PARTITION BY customer_id ORDER BY order_date) AS prev_date FROM orders) t WHERE julianday(order_date) - julianday(prev_date) > 60;",
  },
  {
    id: 59,
    title: "Revenue by Acquisition Channel",
    difficulty: "Medium",
    slug: "sql-inner-join-with-sum-categorical-grouping",
    seoTitle: "SQL INNER JOIN with SUM and Categorical Channel Grouping",
    metaDescription: "Learn how to pull performance financial numbers by mapping cross-table user tags and running total calculations.",
    tags: ["SQL", "INNER JOIN", "SUM Aggregation"],
    description: "Calculate total revenue grouped by acquisition_channel.",
    explanation: "Joining customer acquisition with revenue shows ROI.",
    scenario: "Marketing evaluates channel performance.",
    useCases: ["ROI analysis", "Channel optimization"],
    hint: "JOIN customers and orders",
    starterQuery: "SELECT c.acquisition_channel, SUM(o.total_amount) as revenue \nFROM customers c \nJOIN orders o ON c.customer_id = o.customer_id \nGROUP BY c.acquisition_channel;",
    expectedColumns: ["acquisition_channel", "revenue"],
    expectedRowCount: 4,
    solutionQuery: "SELECT c.acquisition_channel, SUM(o.total_amount) AS revenue FROM customers c JOIN orders o ON c.customer_id = o.customer_id GROUP BY c.acquisition_channel;",
  },
  {
    id: 60,
    title: "Orders with Multiple Product Categories",
    difficulty: "Medium",
    slug: "sql-having-count-distinct-category-keys",
    seoTitle: "SQL HAVING COUNT DISTINCT Category Keys",
    metaDescription: "Learn how to parse relational shopping carts and look up multi-class transactions using HAVING COUNT DISTINCT queries.",
    tags: ["SQL", "COUNT DISTINCT", "HAVING Clause"],
    description: "Find orders that contain products from more than one category.",
    explanation: "COUNT DISTINCT category identifies mixed-category orders.",
    scenario: "Business analyzes cross-selling effectiveness.",
    useCases: ["Cross-sell analysis", "Basket analysis"],
    hint: "COUNT(DISTINCT category)",
    starterQuery: "SELECT oi.order_id \nFROM order_items oi \nJOIN products p ON oi.product_id = p.product_id \nGROUP BY oi.order_id \nHAVING COUNT(DISTINCT p.category) > 1;",
    expectedColumns: ["order_id"],
    expectedRowCount: 3,
    solutionQuery: "SELECT oi.order_id FROM order_items oi JOIN products p ON oi.product_id = p.product_id GROUP BY oi.order_id HAVING COUNT(DISTINCT p.category) > 1;",
  },
  {
    id: 61,
    title: "Average Refund per Order",
    difficulty: "Medium+",
    slug: "sql-avg-function-with-group-by-order-id",
    seoTitle: "SQL AVG Function with GROUP BY Order ID",
    metaDescription: "Learn how to compute numeric dataset means within distinct tracking records by merging the SQL AVG function with GROUP BY.",
    tags: ["SQL", "AVG", "GROUP BY"],
    description: "Calculate average refund amount per order.",
    explanation: "AVG(refund_amount) grouped by order.",
    scenario: "Finance monitors refund impact.",
    useCases: ["Refund analysis", "Financial tracking"],
    hint: "AVG(refund_amount)",
    starterQuery: "SELECT order_id, AVG(refund_amount) as avg_refund \nFROM payments \nGROUP BY order_id;",
    expectedColumns: ["order_id", "avg_refund"],
    expectedRowCount: 5,
    solutionQuery: "SELECT order_id, AVG(refund_amount) AS avg_refund FROM payments GROUP BY order_id;",
  },
  {
    id: 62,
    title: "Orders with High Delivery Fee",
    difficulty: "Medium+",
    slug: "sql-scalar-subquery-where-clause-filtering",
    seoTitle: "SQL Scalar Subquery Configuration within WHERE Filters",
    metaDescription: "Learn how to build comparative row filters against a dynamically resolved mathematical mean inside a nested scalar subquery block.",
    tags: ["SQL", "Subquery", "WHERE Clause"],
    description: "Find orders where delivery_fee exceeds average delivery fee.",
    explanation: "Scalar subquery for comparison.",
    scenario: "Logistics checks high delivery charges.",
    useCases: ["Cost optimization", "Fee analysis"],
    hint: "delivery_fee > AVG(delivery_fee)",
    starterQuery: "SELECT order_id \nFROM orders \nWHERE delivery_fee > (SELECT AVG(delivery_fee) FROM orders);",
    expectedColumns: ["order_id"],
    expectedRowCount: 3,
    solutionQuery: "SELECT order_id FROM orders WHERE delivery_fee > (SELECT AVG(delivery_fee) FROM orders);",
  },
  {
    id: 63,
    title: "Customers with No Successful Payments",
    difficulty: "Medium+",
    slug: "sql-having-sum-case-when-conditional-logic",
    seoTitle: "SQL HAVING SUM with CASE WHEN Conditional Logic",
    metaDescription: "Learn how to flag structural status voids by nesting a conditional case expression inside an aggregate HAVING constraint statement.",
    tags: ["SQL", "HAVING", "CASE WHEN"],
    description: "Find customers whose all payments failed.",
    explanation: "Combining joins and filtering ensures only failed payments exist.",
    scenario: "Support team investigates failed transactions.",
    useCases: ["Failure tracking", "Customer outreach"],
    hint: "HAVING SUM(CASE WHEN success THEN 1 END)=0",
    starterQuery: "SELECT o.customer_id \nFROM orders o \nJOIN payments p ON o.order_id = p.order_id \nGROUP BY o.customer_id \nHAVING SUM(CASE WHEN p.payment_status = 'Success' THEN 1 ELSE 0 END) = 0;",
    expectedColumns: ["customer_id"],
    expectedRowCount: 2,
    solutionQuery: "SELECT o.customer_id FROM orders o JOIN payments p ON o.order_id = p.order_id GROUP BY o.customer_id HAVING SUM(CASE WHEN p.payment_status = 'Success' THEN 1 ELSE 0 END) = 0;",
  },
  {
    id: 64,
    title: "Product Profit per Item",
    difficulty: "Medium+",
    slug: "sql-derived-column-subtraction-arithmetic",
    seoTitle: "SQL Derived Column Columnar Subtraction Arithmetic",
    metaDescription: "Learn how to create computed result aliases at runtime by deploying subtraction arithmetic between separate financial schema keys.",
    tags: ["SQL", "Arithmetic Operations", "Derived Column"],
    description: "Calculate profit per product using price - cost_price.",
    explanation: "Derived column helps measure profitability.",
    scenario: "Finance tracks margins.",
    useCases: ["Profit analysis", "Pricing decisions"],
    hint: "price - cost_price",
    starterQuery: "SELECT product_name, price - cost_price as profit \nFROM products;",
    expectedColumns: ["product_name", "profit"],
    expectedRowCount: 5,
    solutionQuery: "SELECT product_name, price - cost_price AS profit FROM products;",
  },
  {
    id: 65,
    title: "Orders by Customer Type",
    difficulty: "Medium+",
    slug: "sql-inner-join-with-count-group-by-string",
    seoTitle: "SQL INNER JOIN with COUNT and GROUP BY String Categories",
    metaDescription: "Learn how to relate operational ledgers to customer attributes via an INNER JOIN and separate volumes by type tags.",
    tags: ["SQL", "INNER JOIN", "GROUP BY"],
    description: "Count number of orders grouped by customer_type.",
    explanation: "Joining customer attributes enables segmentation.",
    scenario: "Business compares B2B vs B2C performance.",
    useCases: ["Segmentation", "Business reporting"],
    hint: "JOIN customers and orders",
    starterQuery: "SELECT c.customer_type, COUNT(*) as total_orders \nFROM customers c \nJOIN orders o ON c.customer_id = o.customer_id \nGROUP BY c.customer_type;",
    expectedColumns: ["customer_type", "total_orders"],
    expectedRowCount: 3,
    solutionQuery: "SELECT c.customer_type, COUNT(*) AS total_orders FROM customers c JOIN orders o ON c.customer_id = o.customer_id GROUP BY c.customer_type;",
  },
  {
    id: 66,
    title: "Top Rated Products",
    difficulty: "Medium+",
    slug: "sql-multiple-table-joins-with-avg-order-by",
    seoTitle: "SQL Multiple Table Joins with AVG and ORDER BY Sorting",
    metaDescription: "Learn how to pipe structural paths across four normalized system tables to establish sorting by average metric scales.",
    tags: ["SQL", "Multi-Table Join", "AVG Function"],
    description: "Find products associated with highest average feedback rating.",
    explanation: "Join feedback → orders → order_items → products.",
    scenario: "Product team identifies best-rated items.",
    useCases: ["Product improvement", "Customer insights"],
    hint: "AVG(rating) GROUP BY product",
    starterQuery: "SELECT p.product_name, AVG(f.rating) as avg_rating \nFROM feedback f \nJOIN orders o ON f.order_id = o.order_id \nJOIN order_items oi ON o.order_id = oi.order_id \nJOIN products p ON oi.product_id = p.product_id \nGROUP BY p.product_name \nORDER BY avg_rating DESC;",
    expectedColumns: ["product_name", "avg_rating"],
    expectedRowCount: 5,
    solutionQuery: "SELECT p.product_name, AVG(f.rating) AS avg_rating FROM feedback f JOIN orders o ON f.order_id = o.order_id JOIN order_items oi ON o.order_id = oi.order_id JOIN products p ON oi.product_id = p.product_id GROUP BY p.product_name ORDER BY avg_rating DESC;",
  },
  {
    id: 67,
    title: "Orders per Currency",
    difficulty: "Medium+",
    slug: "sql-count-group-by-currency-string",
    seoTitle: "SQL COUNT Function with GROUP BY Currency String",
    metaDescription: "Learn how to partition accounting ledgers and tally record allocations across explicit transaction currency codes.",
    tags: ["SQL", "COUNT", "GROUP BY"],
    description: "Count number of orders grouped by currency.",
    explanation: "Grouping helps multi-currency analysis.",
    scenario: "Finance tracks international transactions.",
    useCases: ["Currency reporting", "Global analysis"],
    hint: "GROUP BY currency",
    starterQuery: "SELECT currency, COUNT(*) as total_orders \nFROM orders \nGROUP BY currency;",
    expectedColumns: ["currency", "total_orders"],
    expectedRowCount: 3,
    solutionQuery: "SELECT currency, COUNT(*) AS total_orders FROM orders GROUP BY currency;",
  },
  {
    id: 68,
    title: "Customers with High Avg Order Value",
    difficulty: "Medium+",
    slug: "sql-having-avg-greater-than-scalar-subquery",
    seoTitle: "SQL HAVING AVG Greater Than Scalar Subquery Baseline",
    metaDescription: "Learn how to build group summaries and drop indexes below a dynamic mean using a nested subquery comparison inside a HAVING block.",
    tags: ["SQL", "HAVING", "Subquery"],
    description: "Find customers whose average order value is above overall average.",
    explanation: "Nested aggregation comparison.",
    scenario: "Marketing targets premium spenders.",
    useCases: ["Customer targeting", "Revenue segmentation"],
    hint: "HAVING AVG(total_amount) > overall AVG",
    starterQuery: "SELECT customer_id, AVG(total_amount) as avg_val \nFROM orders \nGROUP BY customer_id \nHAVING avg_val > (SELECT AVG(total_amount) FROM orders);",
    expectedColumns: ["customer_id", "avg_val"],
    expectedRowCount: 3,
    solutionQuery: "SELECT customer_id, AVG(total_amount) AS avg_val FROM orders GROUP BY customer_id HAVING avg_val > (SELECT AVG(total_amount) FROM orders);",
  },
  {
    id: 69,
    title: "Orders with Maximum Discount Item",
    difficulty: "Medium+",
    slug: "sql-max-aggregate-with-group-by-order-id",
    seoTitle: "SQL MAX Aggregate Function with GROUP BY Order ID",
    metaDescription: "Learn how to identify peak individual transaction line items by passing key indexes inside a localized MAX statement.",
    tags: ["SQL", "MAX", "GROUP BY"],
    description: "Find highest discount applied per order item.",
    explanation: "MAX(discount_amount) grouped by order.",
    scenario: "Marketing evaluates discounts.",
    useCases: ["Discount tracking", "Promo analysis"],
    hint: "MAX(discount_amount)",
    starterQuery: "SELECT order_id, MAX(discount_amount) as max_discount \nFROM order_items \nGROUP BY order_id;",
    expectedColumns: ["order_id", "max_discount"],
    expectedRowCount: 5,
    solutionQuery: "SELECT order_id, MAX(discount_amount) AS max_discount FROM order_items GROUP BY order_id;",
  },
  {
    id: 70,
    title: "Customers by Signup Month",
    difficulty: "Medium+",
    slug: "sql-strftime-date-extraction-group-by",
    seoTitle: "SQL strftime Date Month Extraction and Grouping",
    metaDescription: "Learn how to break down date strings into month integers using strftime tools and group volume logs accordingly.",
    tags: ["SQL", "Date Functions", "strftime"],
    description: "Count customers grouped by signup month.",
    explanation: "Extract month from created_date.",
    scenario: "Growth team tracks acquisition trends.",
    useCases: ["Growth analysis", "Trend tracking"],
    hint: "strftime('%m', created_date)",
    starterQuery: "SELECT strftime('%m', created_date) as month, COUNT(*) as total \nFROM customers \nGROUP BY month;",
    expectedColumns: ["month", "total"],
    expectedRowCount: 6,
    solutionQuery: "SELECT strftime('%m', created_date) AS month, COUNT(*) AS total FROM customers GROUP BY month;",
  },
  {
    id: 71,
    title: "Orders with High Item Count",
    difficulty: "Medium+",
    slug: "sql-having-count-greater-than-nested-subquery-avg",
    seoTitle: "SQL HAVING COUNT Greater Than Nested Subquery Average",
    metaDescription: "Learn how to evaluate complex matrix structures by contrasting localized counts against secondary subquery arrays.",
    tags: ["SQL", "HAVING", "Nested Subquery"],
    description: "Find orders with item count above average.",
    explanation: "Compare grouped counts with overall average.",
    scenario: "Operations identifies bulk orders.",
    useCases: ["Warehouse optimization", "Bulk handling"],
    hint: "HAVING COUNT(*) > AVG",
    starterQuery: "SELECT order_id, COUNT(*) as cnt \nFROM order_items \nGROUP BY order_id \nHAVING cnt > (SELECT AVG(c) FROM (SELECT COUNT(*) as c FROM order_items GROUP BY order_id));",
    expectedColumns: ["order_id", "cnt"],
    expectedRowCount: 3,
    solutionQuery: "SELECT order_id, COUNT(*) AS cnt FROM order_items GROUP BY order_id HAVING cnt > (SELECT AVG(c) FROM (SELECT COUNT(*) AS c FROM order_items GROUP BY order_id));",
  },
  {
    id: 72,
    title: "Revenue Trend by State",
    difficulty: "Medium+",
    slug: "sql-join-with-sum-regional-group-by",
    seoTitle: "SQL JOIN with SUM and Regional String Grouping",
    metaDescription: "Learn how to combine geographic tracking structures with purchase ledgers using clean index matching properties and sum macros.",
    tags: ["SQL", "JOIN", "SUM Aggregation"],
    description: "Calculate revenue grouped by customer state.",
    explanation: "Join customers with orders.",
    scenario: "Business tracks regional performance.",
    useCases: ["Regional analysis", "Expansion planning"],
    hint: "GROUP BY state",
    starterQuery: "SELECT c.state, SUM(o.total_amount) as revenue \nFROM customers c \nJOIN orders o ON c.customer_id = o.customer_id \nGROUP BY c.state;",
    expectedColumns: ["state", "revenue"],
    expectedRowCount: 5,
    solutionQuery: "SELECT c.state, SUM(o.total_amount) AS revenue FROM customers c JOIN orders o ON c.customer_id = o.customer_id GROUP BY c.state;",
  },
  {
    id: 73,
    title: "Orders Delivered by Each Partner",
    difficulty: "Medium+",
    slug: "sql-group-by-delivery-partner",
    seoTitle: "SQL GROUP BY Delivery Partner",
    metaDescription: "Learn how to use GROUP BY and COUNT() to calculate the number of orders handled by each delivery partner.",
    tags: ["SQL", "GROUP BY", "COUNT", "JOIN"],
    description: "Count the total number of orders handled by each delivery partner.",
    explanation: "Use GROUP BY to create one group for each delivery partner and COUNT() to calculate how many orders they have delivered.",
    scenario: "Operations wants to measure each delivery partner's workload.",
    useCases: ["Delivery analytics", "Partner performance", "Operations reporting"],
    hint: "Join orders and delivery_partners using delivery_partner_id, then GROUP BY partner_name.",
    starterQuery: "SELECT dp.partner_name, COUNT(o.order_id) AS total_orders \nFROM delivery_partners dp \nJOIN orders o ON dp.delivery_partner_id = o.delivery_partner_id \nGROUP BY dp.partner_name \nORDER BY total_orders DESC;",
    expectedColumns: ["partner_name", "total_orders"],
    solutionQuery: "SELECT dp.partner_name, COUNT(o.order_id) AS total_orders FROM delivery_partners dp JOIN orders o ON dp.delivery_partner_id = o.delivery_partner_id GROUP BY dp.partner_name ORDER BY total_orders DESC;",
},
  {
    id: 74,
    title: "Customers with High Feedback Volume",
    difficulty: "Medium+",
    slug: "sql-having-count-greater-than-subquery-mean",
    seoTitle: "SQL HAVING COUNT Greater Than Subquery Mean Baseline",
    metaDescription: "Learn how to target top user accounts by verifying category frequencies exceed the baseline produced from deep scalar nested calculations.",
    tags: ["SQL", "HAVING", "Subquery"],
    description: "Find customers who gave more feedback than average.",
    explanation: "Compare feedback counts with average.",
    scenario: "Product team identifies engaged users.",
    useCases: ["User engagement", "Feedback analysis"],
    hint: "HAVING COUNT(*) > AVG",
    starterQuery: "SELECT customer_id, COUNT(*) as cnt \nFROM feedback \nGROUP BY customer_id \nHAVING cnt > (SELECT AVG(c) FROM (SELECT COUNT(*) as c FROM feedback GROUP BY customer_id));",
    expectedColumns: ["customer_id", "cnt"],
    expectedRowCount: 3,
    solutionQuery: "SELECT customer_id, COUNT(*) AS cnt FROM feedback GROUP BY customer_id HAVING cnt > (SELECT AVG(c) FROM (SELECT COUNT(*) AS c FROM feedback GROUP BY customer_id));",
  },
  {
    id: 75,
    title: "Orders with Tax Higher Than Discount",
    difficulty: "Medium+",
    slug: "sql-where-columnar-inequality-operator",
    seoTitle: "SQL WHERE Columnar Inequality Operator Filters",
    metaDescription: "Learn how to filter data grids by asserting mathematical greater-than operations between two separate numeric column keys.",
    tags: ["SQL", "WHERE Clause", "Numeric Comparison"],
    description: "Find orders where tax_amount exceeds discount_amount.",
    explanation: "Simple comparison helps identify pricing structure.",
    scenario: "Finance evaluates tax vs discount impact.",
    useCases: ["Pricing analysis", "Financial review"],
    hint: "tax_amount > discount_amount",
    starterQuery: "SELECT order_id \nFROM orders \nWHERE tax_amount > discount_amount;",
    expectedColumns: ["order_id"],
    expectedRowCount: 4,
    solutionQuery: "SELECT order_id FROM orders WHERE tax_amount > discount_amount;",
  },
  {
    id: 76,
    title: "Orders Without Successful Payment",
    difficulty: "Medium+",
    slug: "sql-left-join-multiple-conditions-is-null",
    seoTitle: "SQL LEFT JOIN with Multiple Conditions and IS NULL Filter",
    metaDescription: "Learn how to locate system exceptions by tracking join criteria changes and filtering unmatched logs via terminal null statements.",
    tags: ["SQL", "LEFT JOIN", "IS NULL"],
    description: "Find orders that do not have any successful payment.",
    explanation: "LEFT JOIN + conditional filtering helps identify orders with no successful transactions.",
    scenario: "Finance wants to track unpaid orders.",
    useCases: ["Payment tracking", "Revenue leakage"],
    hint: "LEFT JOIN payments WHERE success IS NULL",
    starterQuery: "SELECT o.order_id \nFROM orders o \nLEFT JOIN payments p ON o.order_id = p.order_id AND p.payment_status = 'Success' \nWHERE p.payment_id IS NULL;",
    expectedColumns: ["order_id"],
    expectedRowCount: 3,
    solutionQuery: "SELECT o.order_id FROM orders o LEFT JOIN payments p ON o.order_id = p.order_id AND p.payment_status = 'Success' WHERE p.payment_id IS NULL;",
  },
  {
    id: 77,
    title: "Average Order Value by City",
    difficulty: "Medium+",
    slug: "sql-inner-join-with-avg-string-grouping",
    seoTitle: "SQL INNER JOIN with AVG and Location String Grouping",
    metaDescription: "Learn how to generate area benchmark summaries by merging customer profiles with cash ledgers and computing category means.",
    tags: ["SQL", "INNER JOIN", "AVG Function"],
    description: "Calculate average order value for each city.",
    explanation: "Join customers with orders and group by city.",
    scenario: "Business wants city-level performance insights.",
    useCases: ["Geo analysis", "Revenue insights"],
    hint: "GROUP BY city",
    starterQuery: "SELECT c.city, AVG(o.total_amount) as avg_value \nFROM customers c \nJOIN orders o ON c.customer_id = o.customer_id \nGROUP BY c.city;",
    expectedColumns: ["city", "avg_value"],
    expectedRowCount: 5,
    solutionQuery: "SELECT c.city, AVG(o.total_amount) AS avg_value FROM customers c JOIN orders o ON c.customer_id = o.customer_id GROUP BY c.city;",
  },
  {
    id: 78,
    title: "Products with Highest Profit Margin",
    difficulty: "Medium+",
    slug: "sql-order-by-mathematical-ratio-expression",
    seoTitle: "SQL ORDER BY Mathematical Ratio Expressions",
    metaDescription: "Learn how to build advanced analytical rankings by executing proportional floating-point margin math inside sorting blocks.",
    tags: ["SQL", "ORDER BY", "Math Operators"],
    description: "Find products with highest profit margin percentage.",
    explanation: "Profit margin = (price - cost_price) / price.",
    scenario: "Finance wants to prioritize high-margin products.",
    useCases: ["Profit analysis", "Pricing strategy"],
    hint: "(price - cost_price) / price",
    starterQuery: "SELECT product_name, (price - cost_price) * 1.0 / price as margin \nFROM products \nORDER BY margin DESC;",
    expectedColumns: ["product_name", "margin"],
    expectedRowCount: 5,
    solutionQuery: "SELECT product_name, (price - cost_price) * 1.0 / price AS margin FROM products ORDER BY margin DESC;",
  },
  {
    id: 79,
    title: "Customers with Multiple Payment Methods",
    difficulty: "Medium+",
    slug: "sql-having-count-distinct-payment-methods",
    seoTitle: "SQL HAVING COUNT DISTINCT Payment Method Field Matching",
    metaDescription: "Learn how to pinpoint systemic operational diversity flags by comparing count distinct tracking entries against numerical rules.",
    tags: ["SQL", "COUNT DISTINCT", "HAVING Clause"],
    description: "Find customers who have used more than one payment method.",
    explanation: "COUNT DISTINCT identifies variation in behavior.",
    scenario: "Payments team studies user preferences.",
    useCases: ["User behavior", "Payment optimization"],
    hint: "COUNT(DISTINCT payment_method)",
    starterQuery: "SELECT o.customer_id \nFROM orders o \nJOIN payments p ON o.order_id = p.order_id \nGROUP BY o.customer_id \nHAVING COUNT(DISTINCT p.payment_method) > 1;",
    expectedColumns: ["customer_id"],
    expectedRowCount: 3,
    solutionQuery: "SELECT o.customer_id FROM orders o JOIN payments p ON o.order_id = p.order_id GROUP BY o.customer_id HAVING COUNT(DISTINCT p.payment_method) > 1;",
  },
  {
    id: 80,
    title: "Orders with More Than Average Value",
    difficulty: "Medium+",
    slug: "sql-where-greater-than-nested-scalar-subquery",
    seoTitle: "SQL WHERE Greater Than Nested Scalar Subquery Operations",
    metaDescription: "Learn how to pull a single continuous variable from a system-wide average calculation to filter high-value rows dynamically.",
    tags: ["SQL", "Subquery", "WHERE Filter"],
    description: "Find orders whose total_amount is greater than average.",
    explanation: "Scalar subquery compares each row with overall average.",
    scenario: "Business identifies high-value transactions.",
    useCases: ["Benchmarking", "Performance tracking"],
    hint: "total_amount > AVG(total_amount)",
    starterQuery: "SELECT order_id, total_amount \nFROM orders \nWHERE total_amount > (SELECT AVG(total_amount) FROM orders);",
    expectedColumns: ["order_id", "total_amount"],
    expectedRowCount: 4,
    solutionQuery: "SELECT order_id, total_amount FROM orders WHERE total_amount > (SELECT AVG(total_amount) FROM orders);",
  },
  {
    id: 81,
    title: "Previous Order Date for Each Customer",
    difficulty: "Hard",
    slug: "sql-lag-window-function",
    seoTitle: "SQL LAG Window Function Tutorial",
    metaDescription: "Learn how to use the SQL LAG() window function to access values from the previous row within each customer partition.",
    tags: ["SQL", "LAG", "Window Functions"],
    description: "Display each customer's orders along with the date of their previous order.",
    explanation: "The LAG() window function returns the value from the previous row within the same partition. Partition the data by customer_id and order the rows by order_date to retrieve each customer's previous order date.",
    scenario: "The analytics team wants to analyze the time between consecutive purchases for every customer.",
    useCases: ["Customer purchase analysis", "Behavior tracking", "Window function practice"],
    hint: "Use LAG(order_date) OVER (PARTITION BY customer_id ORDER BY order_date).",
    starterQuery: "SELECT customer_id, order_date, LAG(order_date) OVER (PARTITION BY customer_id ORDER BY order_date) AS previous_order_date \nFROM orders;",
    expectedColumns: ["customer_id", "order_date", "previous_order_date"],
    solutionQuery: "SELECT customer_id, order_date, LAG(order_date) OVER (PARTITION BY customer_id ORDER BY order_date) AS previous_order_date FROM orders;",
},
  {
    id: 82,
    title: "Orders with Highest Tax Percentage",
    difficulty: "Hard",
    slug: "sql-order-by-ratio-expression-with-limit",
    seoTitle: "SQL ORDER BY Columnar Percentage Ratio with LIMIT Constraints",
    metaDescription: "Learn how to prioritize top structural values by sorting tables across derived floating ratios and declaring strict row ceilings.",
    tags: ["SQL", "ORDER BY", "LIMIT Clause"],
    description: "Find top 5 orders with highest tax percentage.",
    explanation: "Calculate ratio tax_amount / subtotal.",
    scenario: "Finance audits tax-heavy orders.",
    useCases: ["Tax analysis", "Auditing"],
    hint: "tax_amount / subtotal_amount",
    starterQuery: "SELECT order_id, tax_amount * 1.0 / subtotal_amount as tax_pct \nFROM orders \nORDER BY tax_pct DESC LIMIT 5;",
    expectedColumns: ["order_id", "tax_pct"],
    expectedRowCount: 5,
    solutionQuery: "SELECT order_id, tax_amount * 1.0 / subtotal_amount AS tax_pct FROM orders ORDER BY tax_pct DESC LIMIT 5;",
  },
  {
    id: 83,
    title: "Customers with Total Payments Above 100",
    difficulty: "Hard",
    slug: "sql-group-by-having-sum",
    seoTitle: "SQL GROUP BY and HAVING with SUM",
    metaDescription: "Learn how to use GROUP BY, SUM(), and HAVING to filter customers based on their total payment amount.",
    tags: ["SQL", "JOIN", "GROUP BY", "HAVING", "SUM"],
    description: "Find customers whose total payment amount exceeds 1000.",
    explanation: "Join the orders and payments tables, group the results by customer, calculate the total payment amount using SUM(), and use HAVING to return only customers whose total payments exceed 1000.",
    scenario: "The finance team wants to identify high-value customers based on their total payments.",
    useCases: ["Customer segmentation", "Revenue analysis", "High-value customer identification"],
    hint: "Join orders and payments, GROUP BY customer_id, and use HAVING SUM(amount) > 100.",
    starterQuery: "SELECT o.customer_id, SUM(p.amount) AS total_paid \nFROM orders o \nJOIN payments p ON o.order_id = p.order_id \nGROUP BY o.customer_id \nHAVING SUM(p.amount) > 100;",
    expectedColumns: ["customer_id", "total_paid"],
    solutionQuery: "SELECT o.customer_id, SUM(p.amount) AS total_paid FROM orders o JOIN payments p ON o.order_id = p.order_id GROUP BY o.customer_id HAVING SUM(p.amount) > 100;",
},
  {
    id: 84,
    title: "Orders per Customer per Month",
    difficulty: "Hard",
    slug: "sql-composite-group-by-identifier-and-month-string",
    seoTitle: "SQL Composite GROUP BY Identifier and Month String Configurations",
    metaDescription: "Learn how to configure multi-field matrix logs by clustering data across profile keys and formatted strftime month outputs.",
    tags: ["SQL", "strftime", "Composite GROUP BY"],
    description: "Count number of orders per customer per month.",
    explanation: "Grouping by customer and extracted month.",
    scenario: "Business tracks monthly activity per user.",
    useCases: ["Trend tracking", "User engagement"],
    hint: "strftime('%m', order_date)",
    starterQuery: "SELECT customer_id, strftime('%m', order_date) as month, COUNT(*) as total \nFROM orders \nGROUP BY customer_id, month;",
    expectedColumns: ["customer_id", "month", "total"],
    expectedRowCount: 6,
    solutionQuery: "SELECT customer_id, strftime('%m', order_date) AS month, COUNT(*) AS total FROM orders GROUP BY customer_id, month;",
  },
  {
    id: 85,
    title: "Orders with Highest Delivery Fee per City",
    difficulty: "Hard",
    slug: "sql-inner-join-with-max-regional-string-grouping",
    seoTitle: "SQL INNER JOIN with MAX and Regional String Grouping Constraints",
    metaDescription: "Learn how to query maximum pricing limits inside categorical area tags by linking tables and configuring MAX summaries.",
    tags: ["SQL", "INNER JOIN", "MAX Function"],
    description: "Find highest delivery_fee for each customer city.",
    explanation: "Join customers and group by city.",
    scenario: "Logistics analyzes delivery cost distribution.",
    useCases: ["Cost optimization", "Regional pricing"],
    hint: "MAX(delivery_fee)",
    starterQuery: "SELECT c.city, MAX(o.delivery_fee) as max_fee \nFROM customers c \nJOIN orders o ON c.customer_id = o.customer_id \nGROUP BY c.city;",
    expectedColumns: ["city", "max_fee"],
    expectedRowCount: 5,
    solutionQuery: "SELECT c.city, MAX(o.delivery_fee) AS max_fee FROM customers c JOIN orders o ON c.customer_id = o.customer_id GROUP BY c.city;",
  },
  {
    id: 86,
    title: "Number of Customers per Product",
    difficulty: "Hard",
    slug: "sql-count-distinct-group-by",
    seoTitle: "SQL COUNT DISTINCT with GROUP BY",
    metaDescription: "Learn how to count the number of unique customers who purchased each product using COUNT(DISTINCT).",
    tags: ["SQL", "COUNT DISTINCT", "GROUP BY"],
    description: "For each product, count the number of distinct customers who purchased it.",
    explanation: "Use COUNT(DISTINCT) to count unique customers for each product. GROUP BY creates one group per product, allowing you to calculate customer reach for every product.",
    scenario: "The product team wants to measure how many unique customers have purchased each product.",
    useCases: ["Product popularity analysis", "Customer reach reporting", "Inventory planning"],
    hint: "Join order_items with orders, GROUP BY product_id, and use COUNT(DISTINCT customer_id).",
    starterQuery: "SELECT oi.product_id, COUNT(DISTINCT o.customer_id) AS customer_count \nFROM order_items oi \nJOIN orders o ON oi.order_id = o.order_id \nGROUP BY oi.product_id \nORDER BY customer_count DESC;",
    expectedColumns: ["product_id", "customer_count"],
    solutionQuery: "SELECT oi.product_id, COUNT(DISTINCT o.customer_id) AS customer_count FROM order_items oi JOIN orders o ON oi.order_id = o.order_id GROUP BY oi.product_id ORDER BY customer_count DESC;",
},
  {
    id: 87,
    title: "Customers with Only Failed Orders",
    difficulty: "Hard",
    slug: "sql-having-sum-case-when-status-inequality",
    seoTitle: "SQL HAVING SUM with CASE WHEN Status String Inequality Expressions",
    metaDescription: "Learn how to parse operational pipelines and exclude accounts using specific aggregate condition counters inside a HAVING statement.",
    tags: ["SQL", "HAVING Clause", "CASE WHEN"],
    description: "Find customers whose all orders are cancelled.",
    explanation: "Filter grouped results where only cancelled orders exist.",
    scenario: "Support team targets problematic users.",
    useCases: ["Issue tracking", "Customer support"],
    hint: "HAVING SUM(CASE WHEN order_status != 'Cancelled' THEN 1 END)=0",
    starterQuery: "SELECT customer_id \nFROM orders \nGROUP BY customer_id \nHAVING SUM(CASE WHEN order_status != 'cancelled' THEN 1 ELSE 0 END) = 0;",
    expectedColumns: ["customer_id"],
    expectedRowCount: 2,
    solutionQuery: "SELECT customer_id FROM orders GROUP BY customer_id HAVING SUM(CASE WHEN order_status != 'cancelled' THEN 1 ELSE 0 END) = 0;",
  },
  {
    id: 88,
    title: "Revenue from Verified vs Non-Verified Customers",
    difficulty: "Hard",
    slug: "sql-join-with-sum-and-boolean-flag-grouping",
    seoTitle: "SQL JOIN with SUM and Boolean Status Flag Grouping",
    metaDescription: "Learn how to evaluate user demographic impacts by summing purchase metrics across clean boolean verification indices.",
    tags: ["SQL", "JOIN", "SUM", "Boolean Grouping"],
    description: "Compare revenue between verified and non-verified customers.",
    explanation: "Join customers and group by verification flag.",
    scenario: "Business evaluates trust impact.",
    useCases: ["User quality analysis", "Revenue segmentation"],
    hint: "GROUP BY is_verified",
    starterQuery: "SELECT c.is_verified, SUM(o.total_amount) as revenue \nFROM customers c \nJOIN orders o ON c.customer_id = o.customer_id \nGROUP BY c.is_verified;",
    expectedColumns: ["is_verified", "revenue"],
    expectedRowCount: 2,
    solutionQuery: "SELECT c.is_verified, SUM(o.total_amount) AS revenue FROM customers c JOIN orders o ON c.customer_id = o.customer_id GROUP BY c.is_verified;",
  },
  {
    id: 89,
    title: "Orders with More Than One Category",
    difficulty: "Hard",
    slug: "sql-having-count-distinct-product-category-validation",
    seoTitle: "SQL HAVING COUNT DISTINCT Product Category Validation",
    metaDescription: "Learn how to inspect individual shopping metrics and identify multi-category baskets using standard HAVING COUNT DISTINCT syntax.",
    tags: ["SQL", "COUNT DISTINCT", "HAVING Clause"],
    description: "Find orders containing items from multiple categories.",
    explanation: "COUNT DISTINCT category per order.",
    scenario: "Business evaluates cross-category buying.",
    useCases: ["Basket analysis", "Cross-sell"],
    hint: "COUNT(DISTINCT category)",
    starterQuery: "SELECT oi.order_id \nFROM order_items oi \nJOIN products p ON oi.product_id = p.product_id \nGROUP BY oi.order_id \nHAVING COUNT(DISTINCT p.category) > 1;",
    expectedColumns: ["order_id"],
    expectedRowCount: 3,
    solutionQuery: "SELECT oi.order_id FROM order_items oi JOIN products p ON oi.product_id = p.product_id GROUP BY oi.order_id HAVING COUNT(DISTINCT p.category) > 1;",
  },
  {
    id: 90,
    title: "Average Rating per Order",
    difficulty: "Hard",
    slug: "sql-avg-aggregate-with-group-by-order-key",
    seoTitle: "SQL AVG Aggregate Function with GROUP BY Order Key Reference",
    metaDescription: "Learn how to determine specific row quality averages by routing data markers through an operational query statement bucketed by order IDs.",
    tags: ["SQL", "AVG", "GROUP BY"],
    description: "Calculate average feedback rating per order.",
    explanation: "Grouping feedback by order_id.",
    scenario: "Product team evaluates order satisfaction.",
    useCases: ["Quality tracking", "Customer insights"],
    hint: "AVG(rating)",
    starterQuery: "SELECT order_id, AVG(rating) as avg_rating \nFROM feedback \nGROUP BY order_id;",
    expectedColumns: ["order_id", "avg_rating"],
    expectedRowCount: 5,
    solutionQuery: "SELECT order_id, AVG(rating) AS avg_rating FROM feedback GROUP BY order_id;",
  },
  {
    id: 91,
    title: "Number of Customers by State",
    difficulty: "Hard",
    slug: "sql-group-by-count-customers-state",
    seoTitle: "SQL GROUP BY and COUNT by State",
    metaDescription: "Learn how to group customers by state and count the number of customers in each state using SQL GROUP BY and COUNT().",
    tags: ["SQL", "GROUP BY", "COUNT"],
    description: "Count the number of customers in each state.",
    explanation: "GROUP BY creates one group for each state, and COUNT() returns the number of customers in each group.",
    scenario: "The business wants to understand its customer distribution across different states.",
    useCases: ["Customer distribution", "Regional analysis", "Business reporting"],
    hint: "GROUP BY state and use COUNT(customer_id).",
    starterQuery: "SELECT state, COUNT(customer_id) AS customer_count \nFROM customers \nGROUP BY state \nORDER BY customer_count DESC;",
    expectedColumns: ["state", "customer_count"],
    solutionQuery: "SELECT state, COUNT(customer_id) AS customer_count FROM customers GROUP BY state ORDER BY customer_count DESC;",
},
  {
    id: 92,
    title: "Orders with Maximum Items per Customer",
    difficulty: "Hard",
    slug: "sql-composite-group-by-customer-and-order",
    seoTitle: "SQL Composite GROUP BY Customer and Order Key Relationships",
    metaDescription: "Learn how to break down dataset line distributions by running multiple keys into your database grouping statements.",
    tags: ["SQL", "JOIN", "COUNT", "Composite GROUP BY"],
    description: "Find the order with highest item count per customer.",
    explanation: "Use aggregation grouped by customer and order.",
    scenario: "Operations analyzes heavy orders.",
    useCases: ["Bulk analysis", "Logistics"],
    hint: "COUNT(order_item_id)",
    starterQuery: "SELECT o.customer_id, o.order_id, COUNT(oi.item_id) as item_count \nFROM orders o \nJOIN order_items oi ON o.order_id = oi.order_id \nGROUP BY o.customer_id, o.order_id;",
    expectedColumns: ["customer_id", "order_id", "item_count"],
    expectedRowCount: 6,
    solutionQuery: "SELECT o.customer_id, o.order_id, COUNT(oi.item_id) AS item_count FROM orders o JOIN order_items oi ON o.order_id = oi.order_id GROUP BY o.customer_id, o.order_id;",
  },
  {
    id: 93,
    title: "Revenue by Payment Method",
    difficulty: "Hard",
    slug: "sql-sum-aggregation-grouped-by-payment-string",
    seoTitle: "SQL SUM Aggregation Grouped by Payment Method String",
    metaDescription: "Learn how to total absolute metric volumes using standard column summation linked to matching payment interface types.",
    tags: ["SQL", "SUM", "GROUP BY"],
    description: "Calculate total revenue grouped by payment_method.",
    explanation: "Join payments and orders.",
    scenario: "Finance tracks payment trends.",
    useCases: ["Payment analytics", "Revenue tracking"],
    hint: "GROUP BY payment_method",
    starterQuery: "SELECT payment_method, SUM(amount) as revenue \nFROM payments \nGROUP BY payment_method;",
    expectedColumns: ["payment_method", "revenue"],
    expectedRowCount: 3,
    solutionQuery: "SELECT payment_method, SUM(amount) AS revenue FROM payments GROUP BY payment_method;",
  },
  {
    id: 94,
    title: "Customers with Orders Above Their Average",
    difficulty: "Hard",
    slug: "sql-correlated-subquery-where-clause-inequality",
    seoTitle: "SQL Correlated Subquery WHERE Clause Inequality Validations",
    metaDescription: "Learn how to benchmark individual record indexes by nesting a dynamic self-referencing subquery loop within your filters.",
    tags: ["SQL", "Correlated Subquery", "WHERE Filter"],
    description: "Find orders where customer's order is above their own average.",
    explanation: "Compare each order with customer average using subquery.",
    scenario: "Identify unusually high purchases.",
    useCases: ["Outlier detection", "Behavior analysis"],
    hint: "Compare with AVG per customer",
    starterQuery: "SELECT o.order_id, o.customer_id \nFROM orders o \nWHERE o.total_amount > (SELECT AVG(total_amount) FROM orders WHERE customer_id = o.customer_id);",
    expectedColumns: ["order_id", "customer_id"],
    expectedRowCount: 4,
    solutionQuery: "SELECT o.order_id, o.customer_id FROM orders o WHERE o.total_amount > (SELECT AVG(total_amount) FROM orders WHERE customer_id = o.customer_id);",
  },
  {
    id: 95,
    title: "Products Never Reviewed",
    difficulty: "Hard",
    slug: "sql-multiple-left-joins-with-is-null-filter",
    seoTitle: "SQL Multiple LEFT JOINS with IS NULL Filter Exclusions",
    metaDescription: "Learn how to isolate systemic transactional gaps across three schemas using sequential left joins and null pointer checks.",
    tags: ["SQL", "LEFT JOIN", "IS NULL"],
    description: "Find products that never received feedback.",
    explanation: "LEFT JOIN feedback chain.",
    scenario: "Product team identifies ignored products.",
    useCases: ["Feedback gaps", "Product improvement"],
    hint: "LEFT JOIN and filter NULL",
    starterQuery: "SELECT p.product_name \nFROM products p \nLEFT JOIN order_items oi ON p.product_id = oi.product_id \nLEFT JOIN feedback f ON oi.order_id = f.order_id \nWHERE f.feedback_id IS NULL;",
    expectedColumns: ["product_name"],
    expectedRowCount: 3,
    solutionQuery: "SELECT p.product_name FROM products p LEFT JOIN order_items oi ON p.product_id = oi.product_id LEFT JOIN feedback f ON oi.order_id = f.order_id WHERE f.feedback_id IS NULL;",
  },
  {
    id: 96,
    title: "Orders with Above Average Items Value",
    difficulty: "Hard",
    slug: "sql-having-sum-greater-than-scalar-subquery",
    seoTitle: "SQL HAVING SUM Greater Than Scalar Subquery Mean Baseline",
    metaDescription: "Learn how to isolate aggregate transaction profiles by checking totals against global baseline averages using subqueries.",
    tags: ["SQL", "HAVING SUM", "Scalar Subquery"],
    description: "Find orders whose total item value exceeds average.",
    explanation: "Aggregate item values per order and compare.",
    scenario: "Business identifies large-value orders.",
    useCases: ["Revenue analysis", "Order profiling"],
    hint: "SUM(total_price) per order",
    starterQuery: "SELECT order_id, SUM(total_price) as total \nFROM order_items \nGROUP BY order_id \nHAVING total > (SELECT AVG(total_price) FROM order_items);",
    expectedColumns: ["order_id", "total"],
    expectedRowCount: 3,
    solutionQuery: "SELECT order_id, SUM(total_price) AS total FROM order_items GROUP BY order_id HAVING total > (SELECT AVG(total_price) FROM order_items);",
  },
  {
    id: 97,
    title: "Customers with Frequent Orders",
    difficulty: "Hard",
    slug: "sql-having-count-greater-than-nested-derived-average",
    seoTitle: "SQL HAVING COUNT Greater Than Nested Derived Table Average",
    metaDescription: "Learn how to separate top user frequency metrics by validating row aggregations against a calculated subquery table score.",
    tags: ["SQL", "HAVING COUNT", "Nested Subquery", "Derived Table"],
    description: "Find customers with orders more than average count.",
    explanation: "Compare grouped counts to overall average.",
    scenario: "Identify frequent buyers.",
    useCases: ["Retention strategy", "Customer segmentation"],
    hint: "HAVING COUNT > AVG",
    starterQuery: "SELECT customer_id, COUNT(*) as cnt \nFROM orders \nGROUP BY customer_id \nHAVING cnt > (SELECT AVG(c) FROM (SELECT COUNT(*) as c FROM orders GROUP BY customer_id));",
    expectedColumns: ["customer_id", "cnt"],
    expectedRowCount: 3,
    solutionQuery: "SELECT customer_id, COUNT(*) AS cnt FROM orders GROUP BY customer_id HAVING cnt > (SELECT AVG(c) FROM (SELECT COUNT(*) AS c FROM orders GROUP BY customer_id));",
  },
  {
    id: 98,
    title: "Orders with Multiple Feedback Entries",
    difficulty: "Hard",
    slug: "sql-group-by-order-id-having-count-greater-than-one",
    seoTitle: "SQL GROUP BY Order ID with HAVING COUNT Greater Than One",
    metaDescription: "Learn how to catch target duplicates or multiple event relations inside an isolated tracking index via conditional counting properties.",
    tags: ["SQL", "GROUP BY", "HAVING COUNT"],
    description: "Find orders that have more than one feedback entry.",
    explanation: "GROUP BY order_id HAVING COUNT > 1.",
    scenario: "Data team checks duplicate feedback.",
    useCases: ["Data quality", "User behavior"],
    hint: "COUNT(feedback_id)",
    starterQuery: "SELECT order_id, COUNT(*) as cnt \nFROM feedback \nGROUP BY order_id \nHAVING cnt > 1;",
    expectedColumns: ["order_id", "cnt"],
    expectedRowCount: 2,
    solutionQuery: "SELECT order_id, COUNT(*) AS cnt FROM feedback GROUP BY order_id HAVING cnt > 1;",
  },
  {
    id: 99,
    title: "Revenue by Product Brand",
    difficulty: "Hard",
    slug: "sql-inner-join-with-sum-grouped-by-brand-string",
    seoTitle: "SQL INNER JOIN with SUM Grouped by Brand String Type",
    metaDescription: "Learn how to construct clean commercial line summaries by mapping inventory files to transactions and executing standard groupings.",
    tags: ["SQL", "INNER JOIN", "SUM", "GROUP BY"],
    description: "Calculate total revenue grouped by product brand.",
    explanation: "Join order_items with products.",
    scenario: "Brand performance tracking.",
    useCases: ["Brand analysis", "Sales reporting"],
    hint: "GROUP BY brand",
    starterQuery: "SELECT p.brand, SUM(oi.total_price) as revenue \nFROM order_items oi \nJOIN products p ON oi.product_id = p.product_id \nGROUP BY p.brand;",
    expectedColumns: ["brand", "revenue"],
    expectedRowCount: 4,
    solutionQuery: "SELECT p.brand, SUM(oi.total_price) AS revenue FROM order_items oi JOIN products p ON oi.product_id = p.product_id GROUP BY p.brand;",
  },
  {
    id: 100,
    title: "Average Payment Amount by Payment Method",
    difficulty: "Hard",
    slug: "sql-group-by-avg-payment-method",
    seoTitle: "SQL GROUP BY with AVG Function",
    metaDescription: "Learn how to use SQL GROUP BY and AVG() to calculate the average payment amount for each payment method.",
    tags: ["SQL", "GROUP BY", "AVG", "JOIN"],
    description: "Calculate the average payment amount for each payment method.",
    explanation: "Use GROUP BY to create one group for each payment method, then apply AVG() to calculate the average payment amount collected for that method.",
    scenario: "The finance team wants to compare the average transaction value across different payment methods.",
    useCases: ["Payment analysis", "Financial reporting", "Business intelligence"],
    hint: "GROUP BY payment_method and use AVG(amount).",
    starterQuery: "SELECT payment_method, AVG(amount) AS average_payment \nFROM payments \nGROUP BY payment_method \nORDER BY average_payment DESC;",
    expectedColumns: ["payment_method", "average_payment"],
    solutionQuery: "SELECT payment_method, AVG(amount) AS average_payment FROM payments GROUP BY payment_method ORDER BY average_payment DESC;",
}
                   
];
